import React, { useState, useEffect, useRef, useId } from 'react';
import StarterTaxFields from './StarterTaxFields';
import axios from 'axios';
import { docText, refusalMessage, currentLang, reportBrowserRefusal } from './docText';
import { saveBlob } from './downloadFile';
import { GOLD, NAVY_FORM, NAVY_SHELL } from './colors';
import { FORM_BACK_BTN, FORM_SAVE_BTN, FORM_SUBMIT_BTN } from './theme';
import { useIsNarrow, gridCols, LoadError, activateProps } from './ui';
// S4BGCIV: this form was English only. useTranslation is added for the one new note below,
// which must read in English and French; every other label in this file is untouched.
import { useTranslation } from 'react-i18next';

const API = '/api';
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

// Demo data for each section
const DEMO_S1 = {
  address_line1: '32 Notting Hill Gate',
  address_line2: '',
  city: 'London',
  postcode: 'W11 3HX',
  country_of_residence: 'United Kingdom',
  state_county: 'Greater London',
  personal_email: 'demo.personal@example.com',
  personal_mobile: '+44778901234',
  date_of_birth: '1990-03-15',
  emergency_contact_name: 'Demo Contact',
  emergency_contact_phone: '+44779001234',
  emergency_contact_email: 'demo.contact@example.com',
  rib_bank_name: 'Barclays',
  rib_iban: 'GB29NWBK60161331926819',
  rib_sort_code: '20-00-00',
  bank_account_name: 'Demo Employee',
  gp_name: 'Dr. Emily Clarke',
  gp_address: '10 Kensington Church St, London W8 4EP',
  gp_phone: '+44207 123 4567',
  additional_requirements: '',
  edm_acknowledged: false,
  // TAXTOS1: moved from DEMO_S3 along with the fields themselves.
  title: 'Mr',
  marital_status: 'Single',
  ni_number: 'AB123456C',
  passport_number: '',
  starter_first_job_since_april: false,
  starter_only_job: true,
  starter_other_income: false,
  student_loan_repayment: false,
  student_loan_plan: '',
  p45_provided: true,
  uk_resident: true,
};

const DEMO_FAMILY = {
  partner: { first_name: 'Demo', last_name: 'Partner', date_of_birth: '1988-07-22' },
  children: [
    { first_name: 'Demo', last_name: 'Child', date_of_birth: '2018-05-10' },
  ],
};

const DEMO_S2 = {
  intune_enrollment: true,
  mfa_enrollment: true,
  edm_mobile: false,
  ms365_licence: 'Microsoft 365 E3',
  copilot_licence: true,
  powerbi_licence: true,
  teams_domestic: true,
  sharepoint_sites: ['https://enkolondon.sharepoint.com'],
  distribution_groups: ['Enko London', 'Enko Group', 'EnkoTeam'],
  shared_calendar_permissions: true,
  teams_call_recording_group: 'TeamsCallRecording',
  defender_cloud_apps: true,
  e5_information_protection: true,
  additional_licences: 'Adobe Acrobat Pro (1 seat)',
  approve_licence_purchase: true,
};

const DEMO_S3 = {  employment_type: 'Full-time',
  work_pattern: '',
  contracted_hours: '',
  is_director: false,
  directorship_start_date: '',
  currency: 'GBP',
  salary: '85000',
  working_days_per_week: 5,
  pension_enrolled: true,
  health_insurance_eligible: true,
};

const DISTRIBUTION_GROUPS = [
  'Admin JNB',
  'ECI-Compliance',
  'Enko Cameroon',
  "Enko Côte d'Ivoire",
  'Enko Group',
  'Enko London',
  'Enko South Africa',
  'Enkoprivate',
  'EnkoTeam',
  'IM Group',
  'Operations',
];

const SHAREPOINT_SITES = [
  { label: 'Bosquet Investments Limited', url: 'https://enkolondon.sharepoint.com/sites/BosquetInvestmentsLimited' },
  { label: 'ECCA', url: 'https://enkolondon.sharepoint.com/sites/ECCA' },
  { label: 'ECM SA Managers', url: 'https://enkolondon.sharepoint.com/sites/ECMSAManagers' },
  { label: 'Enko Blotter', url: 'https://enkolondon.sharepoint.com/sites/EnkoReporting' },
  { label: 'Enko Capital LLP Team Site', url: 'https://enkolondon.sharepoint.com' },
  { label: 'Enko Capital Rwanda', url: 'https://enkolondon.sharepoint.com/sites/EnkoCapitalRwanda' },
  { label: 'Enko Principal Investments', url: 'https://enkolondon.sharepoint.com/sites/EnkoPrincipalInvestments' },
  { label: 'Enko Private Equity', url: 'https://enkolondon.sharepoint.com/sites/EnkoPrivateEquity' },
];

// Regulatory classification (Compliance-confirmed, Holly/Craig sign-off). Fixed, definitive
// 8-item Certified Function list.
export const CERTIFIED_FUNCTIONS = [
  'CASS Oversight',
  'Proprietary Trade',
  'Significant Management',
  'Functions requiring qualifications',
  'Manager of Certification Employee(s)',
  'Material Risk Taker',
  'Client Dealing',
  'Algorithmic Trading',
];

// NONCERTLIST: the definitive per-jurisdiction Non-Certified Function list, provided by Compliance
// (replacing the old free-text field, which had a real bug: typed text invisible while typing, no
// way to remove an entry). Grouped by jurisdiction for clarity in the UI. An individual may hold
// several roles across several jurisdictions, not limited to their primary entity. Stored the SAME
// way as before - a flat array of the selected role strings, joined comma-space on submit - so no
// backend/schema/model change is needed. Each stored value is prefixed with its jurisdiction so a
// role name shared across jurisdictions (e.g. "Compliance Officer/MLRO") stays distinct.
export const NON_CERTIFIED_FUNCTION_GROUPS = [
  { jurisdiction: 'UK', roles: ['SMF (Senior Management Function)'] },
  { jurisdiction: 'South Africa', roles: [
    'Key Individual (KI)', 'Cat I Representative', 'Cat II Representative',
    'Compliance Officer/MLRO'] },
  { jurisdiction: 'Mauritius', roles: ['Officer', 'Compliance Officer/MLRO'] },
  { jurisdiction: 'Rwanda', roles: ['CMA Senior Management', 'CMA Key Personnel'] },
  { jurisdiction: 'West Africa', roles: ['AMF regulated function'] },
  { jurisdiction: 'Cameroon', roles: ['COSUMAF regulated function'] },
];
// The stored string for a (jurisdiction, role) pair, e.g. "South Africa - Key Individual (KI)".
// A hyphen-minus, not an em dash (STRICT RULE), and never a comma, so the comma-space join used on
// submit round-trips cleanly.
export const nonCertifiedValue = (jurisdiction, role) => `${jurisdiction} - ${role}`;

const CURRENCIES = [
  { code: 'GBP', label: 'GBP (United Kingdom)' },
  { code: 'USD', label: 'USD (United States)' },
  { code: 'ZAR', label: 'ZAR (South Africa)' },
  { code: 'XOF', label: 'XOF (Ivory Coast)' },
  { code: 'XAF', label: 'XAF (Cameroon)' },
  { code: 'MUR', label: 'MUR (Mauritius)' },
  { code: 'RWF', label: 'RWF (Rwanda)' },
];

const ENTITY_PAYROLL_CONFIG = {
  // SALARYPERIOD: defaultSalaryPeriod sits here beside defaultCurrency so every per-entity
  // payroll default lives in ONE place. Annual for the UK, monthly everywhere else, per the
  // confirmed decision. It is ONLY a default for a row that has none stored yet - the stored
  // value always wins (see the load below), and the user can override the choice freely.
  'Enko Capital Management LLP (UK)':      { defaultCurrency: 'GBP', defaultSalaryPeriod: 'annual',  showPension: true,  showHealthInsurance: true },
  'Enko Capital Investments Pty Ltd (SA)': { defaultCurrency: 'USD', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true },
  'Enko Capital Managers Pty Ltd (SA)':    { defaultCurrency: 'ZAR', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true },
  'ECM SA Managers (Pty) Ltd':             { defaultCurrency: 'ZAR', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true },
  'Enko Fund Managers Ltd (MU)':           { defaultCurrency: 'USD', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true },
  'Enko Capital West Africa (CIV)':        { defaultCurrency: 'XOF', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true },
  // CMRXAF: Cameroon uses XAF (Central African CFA franc), not XOF (West African CFA franc).
  // The XOF here was a copy of the Cote d'Ivoire line above, which is correctly XOF and is
  // deliberately left untouched. XAF was already in the CURRENCIES list, so this is a default
  // correction only, not a new option.
  'Enko Capital Central Africa (CMR)':     { defaultCurrency: 'XAF', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true },
  'Enko Capital Rwanda Ltd (RW)':          { defaultCurrency: 'USD', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true },
};
const DEFAULT_PAYROLL_CONFIG = { defaultCurrency: 'USD', defaultSalaryPeriod: 'monthly', showPension: false, showHealthInsurance: true };

const DEMO_S4 = {
  global_check_required: true,
  local_check_required: true,
  compliance_attestation_signed: true,
  compliance_attestation_date: '2025-01-12',
  certified_functions: [],
  non_certified_functions: [],
  senior_management: false,
  classification_none_confirmed: true,
};

const uploadZoneStyles = {
  zone:        { border: '2px dashed #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center', cursor: 'pointer', backgroundColor: '#fafafa', marginBottom: '8px' },
  zoneActive:  { border: `2px dashed ${NAVY_FORM}`, backgroundColor: '#f0f4ff' },
  fileName:    { fontSize: '13px', color: NAVY_FORM, fontWeight: '500' },
  placeholder: { fontSize: '13px', color: '#999' },
  viewBtn:     { display: 'block', marginBottom: '8px', background: 'none', border: `1px solid ${NAVY_FORM}`, borderRadius: '6px', padding: '6px 12px', fontSize: '12px', color: NAVY_FORM, cursor: 'pointer' },
  uploadBtn:   { width: '100%', padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: NAVY_FORM, color: '#fff', fontSize: '13px', cursor: 'pointer' },
};

const RequiredMark = () => {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '2px' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      <span style={{ color: '#e74c3c', cursor: 'default' }}>*</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)', backgroundColor: NAVY_FORM, color: '#fff',
          padding: '5px 10px', borderRadius: '4px', fontSize: '12px',
          whiteSpace: 'nowrap', zIndex: 200, pointerEvents: 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
        }}>
          This field is required.
          <span style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)', width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: `5px solid ${NAVY_FORM}`
          }} />
        </span>
      )}
    </span>
  );
};

const Spinner = () => (
  <span style={{
    display: 'inline-block', width: '13px', height: '13px',
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#fff', borderRadius: '50%',
    animation: 'spin 0.65s linear infinite',
    marginRight: '8px', verticalAlign: 'middle'
  }} />
);

// NSPAYNAV: `allowedSections` is OPT-IN, defaulting to all 4 (today's exact behaviour) so every
// existing caller (EmployeeProfile.js, App.js's COO/finance mounts, EmployeeView.js) is byte-
// identical. Only the new payroll-shell "New Starter Payroll" worklist mount passes [3], mirroring
// LeaverForm.js's own S3SPLIT allowedSections precedent - `section` itself already only ever
// renders that one section's block (each of the 4 blocks below is independently gated on
// `section === n`), so this exists solely to stop the progress bar's OTHER dots from rendering as
// falsely clickable (NAVAFFORD's isClickable) when the parent's onNavigate is a deliberate no-op.
// S2CONFIRM: review-and-confirm modal shown before Section 2 (IT Setup) is submitted. It lists
// EXACTLY the current Section 2 selections (read straight from the live s2 state, the same object
// submit() serialises) so an accidental checkbox tick can be caught before the IT Setup xlsx/email
// reaches RFA. Pure display + two buttons; it never touches s2, submit(), the payload, or the
// backend. Distribution Groups get their OWN prominent block (the field at the centre of the real
// dispute) and each group family shows "None selected" explicitly when empty, so an unexpected tick
// (or an unexpectedly empty list) is unmistakable rather than hidden by omission.
function Section2ConfirmModal({ s2, isUpdate, onCancel, onConfirm }) {
  // Fixed labels mirroring the Section 2 form's own field labels exactly (no re-interpretation).
  const CHECKBOX_FIELDS = [
    ['intune_enrollment', 'InTune Enrollment'],
    ['mfa_enrollment', 'MFA Authenticator Enrollment'],
    ['edm_mobile', 'EDM Outlook and Teams on personal mobile'],
    ['copilot_licence', 'Copilot Licence'],
    ['powerbi_licence', 'Power BI Pro Licence'],
    ['defender_cloud_apps', 'Microsoft Defender for Cloud Apps'],
    ['e5_information_protection', 'Microsoft 365 E5 Information Protection and Governance'],
    ['teams_domestic', 'Microsoft Teams for Telephone Calls (includes international calling plan)'],
    ['approve_licence_purchase', 'Approve purchasing additional licence seats if needed'],
  ];
  const checkedOptions = CHECKBOX_FIELDS.filter(([k]) => !!s2[k]).map(([, label]) => label);
  if (s2.ms365_licence) checkedOptions.unshift(String(s2.ms365_licence));
  const groups = Array.isArray(s2.distribution_groups) ? s2.distribution_groups : [];
  const sites = Array.isArray(s2.sharepoint_sites) ? s2.sharepoint_sites : [];
  const calendarAnswer = s2.shared_calendar_permissions === true ? 'Yes'
    : s2.shared_calendar_permissions === false ? 'No' : 'Not answered';

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(26,39,68,0.55)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
  };
  const modal = {
    background: '#fff', borderRadius: '12px', padding: '24px 26px', width: '560px',
    maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 96px)', overflowY: 'auto',
    boxShadow: '0 12px 40px rgba(26,39,68,0.28)',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  };
  const groupTitle = { margin: '18px 0 6px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: NAVY_FORM };
  const emptyNote = { margin: '2px 0', fontSize: '13px', color: '#999', fontStyle: 'italic' };
  const listStyle = { margin: '2px 0 0', paddingLeft: '20px', fontSize: '13px', color: '#333', lineHeight: 1.6 };
  const dgBox = { marginTop: '6px', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${groups.length ? GOLD : '#e0e0e0'}`, background: groups.length ? 'rgba(201,168,76,0.08)' : '#fafafa' };

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Confirm IT Setup selections">
      <div style={modal}>
        <h2 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: NAVY_FORM }}>
          Review IT Setup before submitting
        </h2>
        <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#666' }}>
          This is sent to RFA/IT to action. Please check the selections below, especially the
          distribution groups, and correct any accidental tick before confirming.
        </p>

        {/* Distribution Groups FIRST and prominent - the field at the centre of the real dispute. */}
        <p style={groupTitle}>Distribution Groups</p>
        <div style={dgBox}>
          {groups.length === 0 ? (
            <p style={{ margin: 0, fontSize: '13px', color: '#8a6a1f' }}>None selected.</p>
          ) : (
            <ul style={{ ...listStyle, paddingLeft: '18px' }}>
              {groups.map(g => <li key={g}>{g}</li>)}
            </ul>
          )}
        </div>

        <p style={groupTitle}>Licences &amp; Options</p>
        {checkedOptions.length === 0 ? (
          <p style={emptyNote}>None selected.</p>
        ) : (
          <ul style={listStyle}>{checkedOptions.map(o => <li key={o}>{o}</li>)}</ul>
        )}
        {s2.additional_licences && String(s2.additional_licences).trim() && (
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#333' }}>
            <strong>Additional licence requirements:</strong> {String(s2.additional_licences).trim()}
          </p>
        )}

        <p style={groupTitle}>Calendar &amp; Teams</p>
        <ul style={listStyle}>
          <li>Shared calendar permissions: <strong>{calendarAnswer}</strong></li>
          <li>Teams Call Recording Group: <strong>{s2.teams_call_recording_group || 'None'}</strong></li>
        </ul>

        <p style={groupTitle}>SharePoint Sites</p>
        {sites.length === 0 ? (
          <p style={emptyNote}>None selected.</p>
        ) : (
          <ul style={listStyle}>{sites.map(s => <li key={s}>{s}</li>)}</ul>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
          <button
            onClick={onCancel}
            style={{ padding: '10px 18px', fontSize: '14px', borderRadius: '8px', border: `1.5px solid ${NAVY_FORM}`, background: 'none', color: NAVY_FORM, cursor: 'pointer', fontWeight: 600 }}
          >
            Go back and edit
          </button>
          <button
            onClick={onConfirm}
            style={{ padding: '10px 18px', fontSize: '14px', borderRadius: '8px', border: 'none', background: NAVY_FORM, color: '#fff', cursor: 'pointer', fontWeight: 600 }}
          >
            {isUpdate ? 'Confirm & Update' : 'Confirm & Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewStarterForm({ employeeId, employeeName, employeeType, section, completedBy, currentUserRole, onBack, onNavigate, onComplete, employeeEntity, allowedSections: allowedSectionsProp = [1, 2, 3, 4] }) {
  // SASCOPE (2026-10-27, business decision): the compliance role, group officer included, no longer
  // receives Section 3 (payroll); the server withholds its values, and the screen offers no way to it.
  const hidesSection3 = currentUserRole === 'compliance';
  const allowedSections = hidesSection3 ? allowedSectionsProp.filter(n => n !== 3) : allowedSectionsProp;
  // RESPONSIVE Phase 2: two-column field rows (styles.row) reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...styles.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [loading, setLoading] = useState(false);
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState(null);
  // NSLOADFAILSAFE: distinct from the shared `error` string above, which also carries validation
  // and save-failure messages that legitimately get cleared by user actions elsewhere in this
  // file (e.g. setError(null) when moving between S4 steps). formData staying null already
  // correctly disables every submit button (NSGETVISIBLE), but the progress-bar dots read
  // formData?.[`section${n}_completed`] as undefined on EITHER a genuinely fresh, unstarted form
  // OR a failed initial load - the two looked identical once `error` was cleared or scrolled past.
  // This flag is set ONLY by the initial load's own catch and cleared ONLY by that same load
  // succeeding, so it cannot be reset by anything unrelated.
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);

  // Section 1 state
  const [s1, setS1] = useState({
    address_line1: '', address_line2: '', city: '', postcode: '', country_of_residence: '',
    state_county: '',
    personal_email: '', personal_mobile: '', date_of_birth: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_email: '',
    rib_bank_name: '', rib_iban: '', rib_sort_code: '', bank_account_name: '',
    edm_acknowledged: false,
    gp_name: '', gp_address: '', gp_phone: '',
    is_legal_resident: null,
    nationality: '',
    additional_requirements: '',
    // TAXTOS1 (Phase 1 of 3): moved from s3. Same initial values as their Section 3 definitions.
    title: '', marital_status: '',
    ni_number: '', passport_number: '',
    starter_first_job_since_april: false, starter_only_job: false, starter_other_income: false,
    student_loan_repayment: false, student_loan_plan: '',
    p45_provided: false, uk_resident: false,
  });

  // Section 1 collapsible sub-sections
  const [medicalOpen, setMedicalOpen] = useState(true);
  const [familyOpen, setFamilyOpen] = useState(true);
  // ALLOWANCES: repeating allowance lines, mirroring the `family` state above. allowanceTypes is
  // fetched FROM THE SERVER (GET /new-starter/{id}/allowance-types) rather than hardcoded here,
  // so the offered set can never drift from the rule submit_section3 actually enforces.
  const [allowances, setAllowances] = useState([]);
  const [allowanceTypes, setAllowanceTypes] = useState([]);

  // Family members state
  const [family, setFamily] = useState({
    partner: { first_name: '', last_name: '', date_of_birth: '' },
    children: [],
  });

  // Signature & photo upload state (Section 1)
  const [employeeRaw, setEmployeeRaw] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [sigDragOver, setSigDragOver] = useState(false);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // S4ERRSCROLL: the single error banner renders near the top of the form (by the progress
  // stepper), far above the Submit button at the bottom of a long Section 4. A validation
  // early-return called setError but never scrolled, so a reviewer at the bottom saw "nothing
  // happen". This ref + effect scroll the banner into view whenever an error appears, so the
  // message is visible regardless of scroll position. Shared banner => also benefits every
  // section's validation, with no change to any section's logic.
  const errorRef = useRef(null);
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Section 2 state
  const [s2, setS2] = useState({
    intune_enrollment: false, mfa_enrollment: false, edm_mobile: false,
    ms365_licence: '',
    copilot_licence: false, powerbi_licence: false, teams_domestic: false,
    sharepoint_sites: [], distribution_groups: [],
    shared_calendar_permissions: false, teams_call_recording_group: '',
    defender_cloud_apps: false, e5_information_protection: false,
    additional_licences: '',
    // LICAPPROVE: defaults true - the normal answer is "yes, purchase if needed". Matches the
    // column's own DEFAULT TRUE so an untouched form saves as approved.
    approve_licence_purchase: true,
  });

  // S2CONFIRM: when true, the Section 2 review-and-confirm modal is shown. The real submit() only
  // fires from the modal's "Confirm & Submit" button, so an accidental checkbox tick (the confirmed
  // "IM Group" dispute) can be caught here before the IT Setup xlsx/email reaches RFA. submit(),
  // the payload build, and the backend are all untouched - this is purely a pre-submit gate.
  const [showS2Confirm, setShowS2Confirm] = useState(false);

  // Section 3 state
  const [s3, setS3] = useState({
    // TAXTOS1: title, marital_status and the 9 UK Tax/HMRC keys moved to s1 (Section 1).
    employment_type: 'Full-time',
    work_pattern: '', contracted_hours: '',
    is_director: false, directorship_start_date: '',
    currency: '', salary: '', salary_period: '', working_days_per_week: 5,
    // PENSIONWORD / HEALTHINS: only the enrolment/eligibility flags are captured now. The pension
    // provider/contribution-rate and health-insurance provider/enrolment-date fields were removed
    // (moving-target values arranged outside the portal), so they are no longer in s3 and are not
    // sent on submit - the DB columns stay for historical data, they just receive nothing new.
    pension_enrolled: false,
    health_insurance_eligible: false,
  });

  // Section 4 state
  // S4AB: Section 4A records WHICH checks are required; the results and files are HR's, in 4B.
  const [s4, setS4] = useState({
    global_check_required: false,
    // S4BGNOTAVAIL: alternative to a real global check.
    background_check_not_available: false, background_check_not_available_reason: '',
    local_check_required: false,
    compliance_attestation_signed: false, compliance_attestation_date: '',
    certified_functions: [], non_certified_functions: [], senior_management: false,
    classification_none_confirmed: false,
  });
  const [globalCheckFile, setGlobalCheckFile] = useState(null);
  const [localCheckFile, setLocalCheckFile] = useState(null);
  // S4SPLIT (confirmed COO decision, following Holly's feedback): Section 4 is now two sequential
  // sub-steps WITHIN the same Section 4 - 'classification' (the Compliance regulatory function:
  // Certified/Non-Certified Functions, Senior Management, None-of-these) FIRST, since it determines
  // what screening is needed, then 'screening' (background checks + attestation, really an HR
  // function). This is sub-steps inside Section 4, NOT a renumber to Section 5: the single
  // section4_completed flag (read by ~12 files: update_overall_status, NJCSCOPE, hr_report,
  // performance, EmployeeProfile, Dashboard, ...) is preserved, so nothing downstream and no
  // already-completed old-format record is affected. section4_completed is still set only by the
  // FINAL screening-step submit. s4Step drives which sub-step renders.
  // S4AB (2026-10-22): SUPERSEDES S4SPLIT's two compliance sub-steps. Section 4 is now 4A
  // "Background check classification" (compliance: the classification and which checks are
  // required, no file) then 4B "Background check upload" (HR: one file per required check). HR opens
  // Section 4 on 4B and cannot edit 4A; compliance sees 4B read only; the COO and admin act on both.
  const [s4Step, setS4Step] = useState(currentUserRole === 'hr' ? '4b' : 'classification');
  const canUpload4b = ['hr', 'coo', 'admin'].includes(currentUserRole);
  const canEdit4a = currentUserRole !== 'hr';
  // S4AB texts, in the viewer's language, read from the locale files (renders in every test too).
  const s4t = (key, vars) => docText(key, currentLang(), vars);
  // S1SPLIT: Section 1 is split into two sequential sub-steps WITHIN the same section, mirroring
  // the S4SPLIT pattern. 'personal' (Part A) collects the employee's personal details, address,
  // emergency contact, photo and signature, EDM acknowledgement, etc.; 'financial' (Part B)
  // collects the bank details and the UK Tax/HMRC fields. Part A submits to ?part=a, Part B to
  // ?part=b, both with the same buildS1Payload() body. s1Step drives which sub-step renders.
  const { t } = useTranslation();   // S4BGCIV: for the background-check-not-applicable note only.
  // PENSIONFC: Section 3 is translated too. The allowance list names its loop variable "t", so it
  // reaches the translator under this second name.
  const s3T = t;
  const [s1Step, setS1Step] = useState('personal'); // 'personal' (Part A) | 'financial' (Part B)

  // S4CLASSHIDE: single source of truth for "this is a migrated employee whose background check is
  // owned by the COO's upload (auto-validated by MIGRAUTOVAL), so Compliance's Section 4 is
  // classification only". migration_confirmed is the reliable signal - it is exactly what MIGRAUTOVAL
  // and the server-side S4BGVALIDATE gate key on, is set once at migration and never cleared, and is
  // exposed on both employee projections (full and the slim one Compliance gets for another
  // employee). When true: the Background Check sub-step is not rendered at all, the step indicator
  // shows Classification only, and the classification step submits Section 4 directly. Genuine new
  // starters (migration_confirmed falsy) are completely unaffected - both S4SPLIT steps stay.
  const isMigratedClassOnly = employeeRaw?.migration_confirmed === true;

  // S4BGCIV: the OTHER reason Section 4 is classification only - a background check is NOT LEGAL in
  // this employee's entity, so there is nothing to screen and nothing to upload.
  //
  // WHY THIS JOINS isMigratedClassOnly RATHER THAN BEING A SECOND MECHANISM. The two have different
  // causes (one administrative, one legal) but the same consequence for this form: no background
  // check step, and classification submits Section 4 directly. S4CLASSHIDE already implements that
  // presentation, so widening its single flag reuses it whole instead of duplicating five render
  // branches. The two reasons stay separately NAMED so the note below can explain the legal one.
  //
  // MIRRORS THE SERVER, which is the real gate: submit_section4's S4BGCIV branch exempts exactly the
  // same employees via _background_check_not_legal(). If this flag were ever wrong, the server would
  // still decide - the form can only choose not to ASK for something the API would not require.
  //
  // CASE AND WHITESPACE INSENSITIVE, matching canonical_entity's strip().lower() on the server, so a
  // differently cased stored value behaves the same on both sides. A value that does not match is
  // NOT exempt, which is the safe direction: the form asks for a check rather than silently skipping
  // one. Spellings beyond case and whitespace are deliberately not guessed at here either - the
  // server registry is the one place entity spellings are resolved.
  const BG_CHECK_NOT_LEGAL_ENTITIES = ['enko capital west africa (civ)'];
  const bgCheckNotLegal = BG_CHECK_NOT_LEGAL_ENTITIES.includes(
    String(employeeEntity || '').trim().toLowerCase()
  );
  // The union: either reason means Section 4 is classification only.
  const isClassificationOnly = isMigratedClassOnly || bgCheckNotLegal;

  const entityConfig = ENTITY_PAYROLL_CONFIG[employeeEntity] || DEFAULT_PAYROLL_CONFIG;
  const { showPension, showHealthInsurance } = entityConfig;
  const isUK = employeeEntity === 'Enko Capital Management LLP (UK)';
  // Section 2 locks the moment its own IT Setup email fires (final submission, independent of
  // Section 3) - matches submit_section2's own it_email_sent guard, backend.
  const section2Locked = Boolean(formData?.it_email_sent);
  // Section 3/Payroll's own original, untouched lock condition - unrelated to IT's email/lock.
  const cooLocked = Boolean(formData?.section2_completed && formData?.section3_completed);
  // COOREADALL: Section 3 is READ-ONLY when the backend says this viewer cannot write it.
  // can_write_section3 is computed server-side by may_write_section3, which mirrors
  // submit_section3's gate, so the form can never offer a control the API would reject (the same
  // class of defect as the "Mark as already done" button that 400s). Undefined (an older payload,
  // or any endpoint that does not set it) is treated as writable, so behaviour is unchanged for
  // every caller that predates this field - the backend remains the real gate either way.
  const section3ReadOnly = formData?.can_write_section3 === false;
  // One flag for "cannot edit", whether because both sections are submitted (cooLocked, the
  // pre-existing rule) or because this viewer has read-only access to Section 3.
  const section3Locked = cooLocked || section3ReadOnly;

  // ALLOWANCES: which types this employee's country offers, read from the server.
  useEffect(() => {
    if (!employeeId) return;
    axios.get(`${API}/new-starter/${employeeId}/allowance-types`)
      .then(res => setAllowanceTypes(res.data || []))
      .catch(() => setAllowanceTypes([]));   // fail closed: offer nothing if the read fails
  }, [employeeId]);

  const loadFormData = () => {
    setInitialLoadFailed(false);
    axios.get(`${API}/new-starter/${employeeId}`)
      .then(res => {
        const d = res.data;
        setFormData(d);
        setS1(prev => ({
          ...prev,
          date_of_birth: d.date_of_birth || '',
          personal_email: d.personal_email || '',
          personal_mobile: d.personal_mobile || '',
          emergency_contact_name: d.emergency_contact_name || '',
          emergency_contact_phone: d.emergency_contact_phone || '',
          rib_bank_name: d.rib_bank_name || '',
          rib_iban: d.rib_iban || '',
          rib_sort_code: d.rib_sort_code || '',
          bank_account_name: d.bank_account_name || '',
          edm_acknowledged: d.edm_acknowledged ?? false,
          additional_requirements: d.additional_requirements || '',
          // TAXTOS1 (Phase 1 of 3): populated into s1 now, not s3. Same coalescing as before.
          title: d.title || '',
          marital_status: d.marital_status || '',
          ni_number: d.ni_number || '',
          passport_number: d.passport_number || '',
          starter_first_job_since_april: d.starter_first_job_since_april ?? false,
          starter_only_job: d.starter_only_job ?? false,
          starter_other_income: d.starter_other_income ?? false,
          student_loan_repayment: d.student_loan_repayment ?? false,
          student_loan_plan: d.student_loan_plan || '',
          p45_provided: d.p45_provided ?? false,
          uk_resident: d.uk_resident ?? false,
        }));
        setS2({
          intune_enrollment: d.intune_enrollment ?? false,
          mfa_enrollment: d.mfa_enrollment ?? false,
          // EDMMOBILE: restore from the saved form. Was hardcoded false because the backend never
          // returned it (nor stored it); now that it does, reading the saved value makes it reload.
          edm_mobile: d.edm_mobile ?? false,
          ms365_licence: d.ms365_licence || '',
          copilot_licence: d.copilot_licence ?? false,
          powerbi_licence: d.powerbi_licence ?? false,
          teams_domestic: d.teams_domestic ?? false,
          sharepoint_sites: d.sharepoint_sites ? d.sharepoint_sites.split(', ').filter(Boolean) : [],
          distribution_groups: d.distribution_groups ? d.distribution_groups.split(', ').filter(Boolean) : [],
          shared_calendar_permissions: d.shared_calendar_permissions ?? false,
          teams_call_recording_group: d.teams_call_recording_group || '',
          defender_cloud_apps: d.defender_cloud_apps ?? false,
          e5_information_protection: d.e5_information_protection ?? false,
          additional_licences: d.additional_licences || '',
          // LICAPPROVE: ?? true (not || true) so an explicit saved false is preserved on reload;
          // only null/undefined (a form predating this field) falls back to the default.
          approve_licence_purchase: d.approve_licence_purchase ?? true,
        });
        setS3({
          // TAXTOS1: title/marital_status and the 9 UK Tax/HMRC keys are populated into s1 above.
          employment_type: d.employment_type || 'Full-time',
          work_pattern: d.work_pattern || '',
          contracted_hours: d.contracted_hours || '',
          is_director: d.is_director ?? false,
          directorship_start_date: d.directorship_start_date || '',
          currency: d.currency || entityConfig.defaultCurrency,
          salary: d.salary || '',
          // SALARYPERIOD: the STORED period always wins; the entity default only fills a row
          // that has none yet, and the user can change the choice freely either way.
          salary_period: d.salary_period || entityConfig.defaultSalaryPeriod,
          working_days_per_week: d.working_days_per_week ?? 5,
          pension_enrolled: d.pension_enrolled ?? false,
          health_insurance_eligible: d.health_insurance_eligible ?? false,
          // PENSIONWORD / HEALTHINS: pension provider/contribution and health-insurance
          // provider/enrolment-date are no longer loaded into or sent from the form.
        });
        // ALLOWANCES: load this employee's existing lines so a resubmit sends them back intact.
        setAllowances((d.allowances || []).map(a => ({
          allowance_type: a.allowance_type || '',
          amount: a.amount || '',
          currency: a.currency || '',
          period: a.period || '',
        })));
        setS4({
          global_check_required: d.global_check_required ?? false,
          background_check_not_available: d.background_check_not_available ?? false,
          background_check_not_available_reason: d.background_check_not_available_reason || '',
          local_check_required: d.local_check_required ?? false,
          compliance_attestation_signed: d.compliance_attestation_signed ?? false,
          compliance_attestation_date: d.compliance_attestation_date || '',
          certified_functions: d.certified_functions ? d.certified_functions.split(', ').filter(Boolean) : [],
          non_certified_functions: d.non_certified_functions ? d.non_certified_functions.split(', ').filter(Boolean) : [],
          senior_management: d.senior_management ?? false,
          classification_none_confirmed: d.classification_none_confirmed ?? false,
        });
      })
      // NSGETVISIBLE: surface a failure to LOAD the form instead of swallowing it. This GET
      // populates formData AND setS4(...); if it fails (e.g. a 403), the old .catch(console.error)
      // left formData null - disabling Submit into a silent no-op - and never pre-filled Section 4.
      // Now the user sees why, matching S4BTNFIX/S4ERRSCROLL's "no silent failure" principle.
      // NSLOADFAILSAFE: also sets the dedicated flag the progress bar checks, so a failed load
      // reads as "unknown" rather than silently degrading to the same grey dots a genuinely
      // fresh form shows, even if this error string later gets cleared by an unrelated action.
      .catch(err => {
        console.error(err);
        setError(formatError(err.response?.data?.detail) + ' (could not load the form)');
        setInitialLoadFailed(true);
      });
    axios.get(`${API}/employees/${employeeId}`)
      .then(res => setEmployeeRaw(res.data))
      // S4BTNFIX: surface this failure instead of swallowing it. It used to be a silent no-op,
      // which (combined with the removed !employeeRaw button gate) is exactly how a failed fetch
      // silently blocked submission with zero signal. Section 4 no longer depends on employeeRaw,
      // so this is now purely diagnostic - but a real failure is at least visible in the console.
      .catch(err => console.warn('employeeRaw fetch failed (GET /employees/%s):', employeeId, err));
  };

  useEffect(() => {
    loadFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // Populate new employee-level Section 1 fields from the employee record
  useEffect(() => {
    if (!employeeRaw) return;
    setS1(prev => ({
      ...prev,
      address_line1: employeeRaw.address_line1 || '',
      address_line2: employeeRaw.address_line2 || '',
      city: employeeRaw.city || '',
      postcode: employeeRaw.postcode || '',
      country_of_residence: employeeRaw.country_of_residence || '',
      state_county: employeeRaw.state_county || '',
      emergency_contact_email: employeeRaw.emergency_contact_email || '',
      gp_name: employeeRaw.gp_name || '',
      gp_address: employeeRaw.gp_address || '',
      gp_phone: employeeRaw.gp_phone || '',
      bank_account_name: prev.bank_account_name || employeeRaw.bank_account_name || '',
      is_legal_resident: employeeRaw.is_legal_resident ?? null,
      nationality: employeeRaw.nationality || '',
    }));
    const members = employeeRaw.family_members || [];
    const partner = members.find(m => m.relation_type === 'partner');
    const children = members.filter(m => m.relation_type === 'child');
    setFamily({
      partner: partner
        ? { first_name: partner.first_name || '', last_name: partner.last_name || '', date_of_birth: partner.date_of_birth || '' }
        : { first_name: '', last_name: '', date_of_birth: '' },
      children: children.map(c => ({ first_name: c.first_name || '', last_name: c.last_name || '', date_of_birth: c.date_of_birth || '' })),
    });
  }, [employeeRaw]);

  const uploadProfileFile = async (type, file, setFile, setUploading) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png'].includes(ext)) {
      // UPLOADREFUSE: the reason and what to do, translated, and reported to the server.
      setError(docText('uploadChecks.imageOnly', currentLang()));
      reportBrowserRefusal('file_type_blocked', type);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/employees/${employeeId}/${type}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(`${type === 'signature' ? 'Signature' : 'Photo'} uploaded successfully.`);
      setTimeout(() => setSuccess(null), 3000);
      setFile(null);
      const empRes = await axios.get(`${API}/employees/${employeeId}`);
      setEmployeeRaw(empRes.data);
      const nsRes = await axios.get(`${API}/new-starter/${employeeId}`);
      setFormData(nsRes.data);
    } catch (err) {
      // UPLOADREFUSE: the server's real reason, translated when it carries a reason code.
      setError(refusalMessage(err, currentLang(), 'uploadChecks.uploadFailed'));
    }
    setUploading(false);
  };

  // Discard a staged (not-yet-definitive) photo/signature upload - the × button shown
  // while the file is only in Temp/ (section 1 not yet finally submitted).
  const clearStagedFile = async (type) => {
    try {
      await axios.delete(`${API}/employees/${employeeId}/${type}/temp`);
      const nsRes = await axios.get(`${API}/new-starter/${employeeId}`);
      setFormData(nsRes.data);
    } catch {
      setError('Could not clear the staged file. Please try again.');
    }
  };

  // Clear a locally-selected (not yet uploaded) photo/signature file - nothing has been sent
  // to the server yet, so this is local state only. stopPropagation keeps the click from
  // bubbling to the drop zone's own onClick, which would otherwise reopen the file picker.
  const clearLocalFile = (e, setFile, inputId) => {
    e.stopPropagation();
    setFile(null);
    const input = document.getElementById(inputId);
    if (input) input.value = '';
  };

  const viewS1File = async (type) => {
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/${type}/view`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Could not open file. Please try again.');
    }
  };

  // DLNAME: the download counterpart, named by the server. The preview above is unchanged.
  const downloadS1File = async (type) => {
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/${type}/view`, { responseType: 'blob' });
      saveBlob(res);
    } catch {
      setError('Could not download file. Please try again.');
    }
  };

  // ALLOWANCES: include the list ONLY when this country actually offers allowance types.
  // Omitting the key entirely is the deliberate no-op the backend's exclude_unset gating relies
  // on, so a country with no allowances can never clear another country's lines, and a form that
  // never rendered the section cannot wipe what it never showed.
  const buildAllowancesPayload = () => {
    if (!allowanceTypes.length) return {};
    return {
      allowances: allowances
        .filter(a => (a.amount || '').trim() || (a.currency || '').trim())
        .map(a => ({
          allowance_type: a.allowance_type,
          amount: (a.amount || '').trim() || null,
          currency: (a.currency || '').trim() || null,
          period: a.period || null,
        })),
    };
  };

  const sanitize = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj)) out[k] = v === '' ? null : v;
    return out;
  };

  const formatError = (detail) => {
    if (!detail) return 'An error occurred';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg).join('; ');
    return JSON.stringify(detail);
  };

  const buildS4FormData = (isDraft) => {
    const fd = new FormData();
    fd.append('draft', String(isDraft));
    // certified_functions/non_certified_functions are arrays in s4 state (needed for checkbox
    // .includes()/tag-list rendering) - joined explicitly here with ', ' (matching the existing
    // sharepoint_sites/distribution_groups CSV convention) rather than relying on the generic
    // String(v) below, which would join a bare array with an unadorned comma instead.
    Object.entries({
      ...s4,
      certified_functions: s4.certified_functions.join(', '),
      non_certified_functions: s4.non_certified_functions.join(', '),
      completed_by: completedBy,
    }).forEach(([k, v]) => fd.append(k, String(v)));
    // S4AB: no file is ever sent with Section 4A. Compliance attaches no background check file.
    return fd;
  };

  // S4AB: Section 4B, HR uploads one file for each check 4A requires.
  const uploadSection4b = async () => {
    setLoading(true);
    setError(null);
    const required = formData?.section4b_required_checks || [];
    const fd = new FormData();
    let count = 0;
    if (required.includes('Global') && globalCheckFile) { fd.append('global_check_file', globalCheckFile); count += 1; }
    if (required.includes('Local') && localCheckFile) { fd.append('local_check_file', localCheckFile); count += 1; }
    if (count === 0) {
      setError(s4t('section4ab.errChooseFile'));
      setLoading(false); return;
    }
    try {
      await axios.post(`${API}/new-starter/${employeeId}/section4b`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setGlobalCheckFile(null);
      setLocalCheckFile(null);
      setSuccess(s4t('section4ab.uploaded'));
      setTimeout(() => setSuccess(null), 2500);
      loadFormData();
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const buildS1Payload = () => {
    const family_members = [];
    const p = family.partner;
    if (p.first_name || p.last_name || p.date_of_birth) {
      family_members.push({ relation_type: 'partner', first_name: p.first_name || null, last_name: p.last_name || null, date_of_birth: p.date_of_birth || null });
    }
    family.children.forEach(c => {
      if (c.first_name || c.last_name || c.date_of_birth) {
        family_members.push({ relation_type: 'child', first_name: c.first_name || null, last_name: c.last_name || null, date_of_birth: c.date_of_birth || null });
      }
    });
    return { ...sanitize(s1), family_members };
  };

  const saveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      if (section === 4) {
        await axios.post(`${API}/new-starter/${employeeId}/section4`, buildS4FormData(true), {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        let payload;
        if (section === 1) payload = buildS1Payload();
        else if (section === 2) payload = sanitize({ ...s2, sharepoint_sites: s2.sharepoint_sites.join(', '), distribution_groups: s2.distribution_groups.join(', '), completed_by: completedBy });
        else if (section === 3) payload = { ...sanitize({ ...s3, completed_by: completedBy }), ...buildAllowancesPayload() };
        await axios.post(`${API}/new-starter/${employeeId}/section${section}?draft=true`, payload);
      }
      setSuccess('Draft saved.');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (section === 1 && !s1.edm_acknowledged) {
        setError('Please acknowledge the EDM policy before submitting.');
        setLoading(false); return;
      }
      if (section === 1 && !employeeRaw?.photo_url && !formData?.photo_temp_url) {
        setError(photoFile
          ? 'Please click "Upload Photo" to upload your photo before submitting.'
          : 'A photo is required. Please upload a photo before submitting.');
        setLoading(false); return;
      }
      if (section === 4) {
        const hasClassification = s4.certified_functions.length > 0
          || s4.non_certified_functions.length > 0
          || s4.senior_management;
        if (!hasClassification && !s4.classification_none_confirmed) {
          setError('Select at least one classification (Certified Function, Non-Certified Function, or Senior Management), or explicitly confirm "None of these apply".');
          setLoading(false); return;
        }
        // S4AB: Section 4A mirrors the server. Where a background check applies (not an intern, not
        // migrated, not an entity where it is not legal), compliance says which checks are required,
        // or that none is available (with a reason). No file: HR uploads in Section 4B.
        if (employeeType !== 'intern' && !isClassificationOnly) {
          if (s4.background_check_not_available) {
            if (!s4.background_check_not_available_reason.trim()) {
              setError('Please provide a reason for marking the background check as not available.');
              setLoading(false); return;
            }
          } else if (!s4.global_check_required && !s4.local_check_required) {
            setError(s4t('section4ab.errNoChecks'));
            setLoading(false); return;
          }
        }
        await axios.post(`${API}/new-starter/${employeeId}/section4`, buildS4FormData(false), {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        let payload;
        if (section === 1) payload = buildS1Payload();
        else if (section === 2) payload = sanitize({ ...s2, sharepoint_sites: s2.sharepoint_sites.join(', '), distribution_groups: s2.distribution_groups.join(', '), completed_by: completedBy });
        else if (section === 3) payload = { ...sanitize({ ...s3, completed_by: completedBy }), ...buildAllowancesPayload() };
        await axios.post(`${API}/new-starter/${employeeId}/section${section}`, payload);
      }
      setSuccess('Section submitted successfully !');
      setTimeout(() => onComplete(), 1500);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  // S1SPLIT: Section 1's final submit is now split per sub-step. Both parts post the SAME
  // buildS1Payload() body (Part B's bank/tax fields already live in s1 state, so buildS1Payload
  // sends everything); only the ?part query differs. Part A ('a') carries the EDM and photo gates
  // (those fields live in Part A), advances to the financial step on success, and does NOT call
  // onComplete. Part B ('b') has no extra validation (bank/tax fields are intentionally optional,
  // matching prior behaviour) and calls onComplete on success, exactly as the old submit did.
  const submitPartA = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!s1.edm_acknowledged) {
        setError('Please acknowledge the EDM policy before submitting.');
        setLoading(false); return;
      }
      if (!employeeRaw?.photo_url && !formData?.photo_temp_url) {
        setError(photoFile
          ? 'Please click "Upload Photo" to upload your photo before submitting.'
          : 'A photo is required. Please upload a photo before submitting.');
        setLoading(false); return;
      }
      await axios.post(`${API}/new-starter/${employeeId}/section1?part=a`, buildS1Payload());
      setS1Step('financial');
      setSuccess('Personal information saved. Continue with financial and tax details.');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const submitPartB = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/new-starter/${employeeId}/section1?part=b`, buildS1Payload());
      setSuccess('Section submitted successfully !');
      setTimeout(() => onComplete(), 1500);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const sectionLabels = {
    1: 'Personal Information',
    2: 'IT Setup',
    3: 'Payroll and Benefits',
    4: 'Compliance'
  };

  const sectionOwners = {
    1: 'Employee',
    2: 'COO',
    3: 'COO',
    4: 'Compliance'
  };

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.header}>
        <h1 style={styles.title}>New Starter Form</h1>
        <p style={styles.subtitle}>{employeeName}</p>
      </div>

      {/* Section indicator */}
      {/* NSPROGHIDE (confirmed COO decision): hidden entirely on the employee's own view. This
          bar only ever renders Sections 2/3/4 (never Section 1, the one section the employee
          fills), all staff-owned, so for the employee it was three greyed dots carrying no
          status they can act on while exposing the internal COO/Compliance ownership labels.
          Gated on the SAME currentUserRole signal NAVAFFORD established - absent only on
          EmployeeView.js's own-profile call site, present at every staff call site
          (EmployeeProfile.js, App.js) - so NAVAFFORD's clickable-affordance fix inside the map
          below is untouched and staff views are byte-identical. */}
      {/* NSLOADFAILSAFE: while the initial load has failed, the progress dots below cannot
          distinguish "genuinely unstarted" from "unknown" (formData is null either way), so they
          are replaced entirely with a visible error and a working retry rather than rendered
          alongside a message that could be missed or later cleared by an unrelated action. */}
      {initialLoadFailed ? (
        <LoadError message="Could not load this form's saved progress." onRetry={loadFormData} />
      ) : currentUserRole && (
      <div style={styles.progress}>
        {[2, 3, 4].filter(n => allowedSections.includes(n)).map(n => {
          const isCompleted = formData?.[`section${n}_completed`];
          const isCurrent = section === n;
          // NAVAFFORD: currentUserRole is only ever omitted on the employee's own view
          // (EmployeeView.js) - every staff call site (EmployeeProfile.js, App.js) passes it.
          // That view has no onNavigate handler either, so these dots were clickable-looking but
          // dead for the employee; staff views keep their existing clickable affordance untouched.
          // NSPAYNAV: sections outside allowedSections are filtered out above, so the payroll
          // worklist mount (allowedSections=[3]) never shows a dot for a section it cannot open.
          const isClickable = !isCurrent && !!currentUserRole && allowedSections.includes(n);
          return (
            <div key={n}
              style={{ ...styles.progressItem, cursor: isClickable ? 'pointer' : 'default' }}
              onClick={() => isClickable && onNavigate && onNavigate(n)}
              title={isClickable ? `Go to ${sectionLabels[n]}` : undefined}>
              <div style={{
                ...styles.progressDot,
                backgroundColor: isCurrent ? NAVY_FORM : isCompleted ? '#27ae60' : '#ddd',
                boxShadow: isClickable ? '0 0 0 2px #27ae60' : 'none'
              }}>
                {!isCurrent && isCompleted ? '✓' : n}
              </div>
              <div>
                <p style={{ ...styles.progressLabel, color: isCurrent ? NAVY_FORM : isCompleted ? '#555' : '#667085' }}>
                  {sectionLabels[n]}
                </p>
                <p style={styles.progressOwner}>{sectionOwners[n]}</p>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {error && <div ref={errorRef} style={styles.error} role="alert" aria-live="assertive">{error}</div>}
      {success && <div style={styles.successMsg}>{success}</div>}

      {/* ─── SECTION 1 - Employee ─── */}
      {section === 1 && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Section 1: Personal Information</h2>
            {isTestAccount() && <button style={styles.demoBtn} onClick={() => { setS1(DEMO_S1); setFamily(DEMO_FAMILY); }}>Fill demo data</button>}
          </div>

          {/* S1SPLIT: two sequential sub-steps inside Section 1, modelled on the S4SPLIT stepper.
              Step 1 Personal Info (Part A), Step 2 Financial & Tax (Part B). Only the 'personal'
              item is clickable, and only to go back from 'financial'; the 'financial' item never
              navigates. Dot colours match S4SPLIT (NAVY_FORM current, #27ae60 done, #ddd otherwise). */}
          <div style={styles.s4Steps}>
            {[['personal', 'Personal Info'], ['financial', 'Financial & Tax']].map(([key, label], idx) => {
              const isCurrent = s1Step === key;
              const isDone = key === 'personal' && s1Step === 'financial';
              const canGoBack = key === 'personal' && s1Step === 'financial';
              return (
                <div key={key}
                  style={{ ...styles.s4StepItem, cursor: canGoBack ? 'pointer' : 'default' }}
                  onClick={() => canGoBack && setS1Step('personal')}
                  title={canGoBack ? 'Back to personal info' : undefined}>
                  <div style={{ ...styles.s4StepDot, backgroundColor: isCurrent ? NAVY_FORM : isDone ? '#27ae60' : '#ddd' }}>
                    {isDone && !isCurrent ? '✓' : idx + 1}
                  </div>
                  <span style={{ ...styles.s4StepLabel, color: isCurrent ? NAVY_FORM : isDone ? '#555' : '#667085' }}>{label}</span>
                </div>
              );
            })}
          </div>

          {/* ── PART A: PERSONAL INFO ── */}
          {s1Step === 'personal' && (
          <>
          {/* ── 1. PERSONAL DETAILS ── */}
          <div style={styles.fieldGroup}>
            <p style={styles.subBlockLabel}>Personal Details</p>
            <div style={{ ...styles.field, maxWidth: '240px' }}>
              <label style={styles.label} htmlFor={`${a11yId}-date-of-birth`}>Date of Birth <RequiredMark /></label>
              
              <input id={`${a11yId}-date-of-birth`} style={styles.input} type="date" value={s1.date_of_birth}
                onChange={e => setS1({...s1, date_of_birth: e.target.value})} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-address-line-1`}>Address Line 1 <RequiredMark /></label>
              
              <input id={`${a11yId}-address-line-1`} style={styles.input} value={s1.address_line1}
                onChange={e => setS1({...s1, address_line1: e.target.value})}
                placeholder="Street address" />
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-address-line-2`}>Address Line 2</label>
              
              <input id={`${a11yId}-address-line-2`} style={styles.input} value={s1.address_line2}
                onChange={e => setS1({...s1, address_line2: e.target.value})}
                placeholder="Apartment, suite, etc. (optional)" />
            </div>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-city`}>City <RequiredMark /></label>
                
                <input id={`${a11yId}-city`} style={styles.input} value={s1.city}
                  onChange={e => setS1({...s1, city: e.target.value})}
                  placeholder="City" />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-postcode-zip`}>Postcode / ZIP</label>
                
                <input id={`${a11yId}-postcode-zip`} style={styles.input} value={s1.postcode}
                  onChange={e => setS1({...s1, postcode: e.target.value})}
                  placeholder="e.g. W11 3HX" />
              </div>
            </div>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-country`}>Country <RequiredMark /></label>
                
                <input id={`${a11yId}-country`} style={styles.input} value={s1.country_of_residence}
                  onChange={e => setS1({...s1, country_of_residence: e.target.value})}
                  placeholder="Country of residence" />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-state-county`}>State / County</label>
                
                <input id={`${a11yId}-state-county`} style={styles.input} value={s1.state_county}
                  onChange={e => setS1({...s1, state_county: e.target.value})}
                  placeholder="e.g. Greater London" />
              </div>
            </div>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-personal-email`}>Personal Email</label>
                
                <input id={`${a11yId}-personal-email`} style={styles.input} type="email" value={s1.personal_email}
                  onChange={e => setS1({...s1, personal_email: e.target.value})}
                  placeholder="personal@email.com" />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-personal-mobile`}>Personal Mobile</label>
                
                <input id={`${a11yId}-personal-mobile`} style={styles.input} value={s1.personal_mobile}
                  onChange={e => setS1({...s1, personal_mobile: e.target.value})}
                  placeholder="+44..." />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Legal Resident of Country of Employment</label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                <button
                  type="button"
                  style={{ ...styles.input, width: 'auto', padding: '6px 20px', cursor: 'pointer',
                    background: s1.is_legal_resident === true ? '#2D4A3E' : '#fff',
                    color: s1.is_legal_resident === true ? '#fff' : '#333',
                    border: `1px solid ${s1.is_legal_resident === true ? '#2D4A3E' : '#ddd'}`,
                    borderRadius: '6px', fontWeight: s1.is_legal_resident === true ? '600' : '400' }}
                  onClick={() => setS1({...s1, is_legal_resident: true})}>
                  Yes
                </button>
                <button
                  type="button"
                  style={{ ...styles.input, width: 'auto', padding: '6px 20px', cursor: 'pointer',
                    background: s1.is_legal_resident === false ? '#2D4A3E' : '#fff',
                    color: s1.is_legal_resident === false ? '#fff' : '#333',
                    border: `1px solid ${s1.is_legal_resident === false ? '#2D4A3E' : '#ddd'}`,
                    borderRadius: '6px', fontWeight: s1.is_legal_resident === false ? '600' : '400' }}
                  onClick={() => setS1({...s1, is_legal_resident: false})}>
                  No
                </button>
              </div>
            </div>
          </div>

          {/* ── 1b. PASSPORT DETAILS ── */}
          <div style={styles.fieldGroup}>
            <p style={styles.subBlockLabel}>Passport Details</p>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-passport-country`}>Passport country</label>
              
              <input id={`${a11yId}-passport-country`} style={styles.input} value={s1.nationality}
                onChange={e => setS1({...s1, nationality: e.target.value})}
                placeholder="e.g. British" />
            </div>
          </div>

          {/* ── 3. EMERGENCY CONTACT ── */}
          <div style={styles.fieldGroup}>
            <p style={styles.subBlockLabel}>Emergency Contact</p>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-name`}>Name <RequiredMark /></label>
                
                <input id={`${a11yId}-name`} style={styles.input} value={s1.emergency_contact_name}
                  onChange={e => setS1({...s1, emergency_contact_name: e.target.value})}
                  placeholder="Full name" />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-phone`}>Phone <RequiredMark /></label>
                
                <input id={`${a11yId}-phone`} style={styles.input} value={s1.emergency_contact_phone}
                  onChange={e => setS1({...s1, emergency_contact_phone: e.target.value})}
                  placeholder="+44..." />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-email`}>Email</label>
              
              <input id={`${a11yId}-email`} style={styles.input} type="email" value={s1.emergency_contact_email}
                onChange={e => setS1({...s1, emergency_contact_email: e.target.value})}
                placeholder="emergency@email.com" />
            </div>
          </div>

          {/* ── 4. MEDICAL INFORMATION - collapsible ── */}
          <div style={styles.fieldGroup}>
            {/* KBDACCESS: the header was a bare div with onClick, so a keyboard user could not
                open the section at all. aria-expanded announces the state; the style and onClick
                are untouched, so it looks and behaves identically for a mouse user. */}
            <div style={styles.collapsibleHeader} onClick={() => setMedicalOpen(o => !o)}
              aria-expanded={medicalOpen}
              {...activateProps(() => setMedicalOpen(o => !o), { label: 'Medical Information' })}>
              <p style={{ ...styles.subBlockLabel, margin: 0 }}>Medical Information</p>
              <span style={{ ...styles.chevron, transform: medicalOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
            </div>
            {/* KBDACCESS: maxHeight:0 + overflow:hidden CLIPS the fields visually but leaves them
                in the tab order, so someone could Tab into and type in a field they cannot see.
                `inert` removes the subtree from focus and assistive technology entirely, which is
                what actually closes that gap; `visibility: hidden` is the fallback that does the
                same in browsers without inert support. Both apply ONLY while collapsed, so the
                open section and every mouse interaction are completely unaffected, and the
                max-height transition still drives the animation exactly as before. */}
            <div style={{ overflow: 'hidden', maxHeight: medicalOpen ? '600px' : '0', transition: 'max-height 0.3s ease', visibility: medicalOpen ? 'visible' : 'hidden' }}
              {...(medicalOpen ? {} : { inert: true })}>
              <div style={{ paddingTop: '16px' }}>
                <div style={rowStyle}>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-gp-name`}>GP Name</label>
                    
                    <input id={`${a11yId}-gp-name`} style={styles.input} value={s1.gp_name}
                      onChange={e => setS1({...s1, gp_name: e.target.value})}
                      placeholder="Dr. Full Name" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-gp-phone`}>GP Phone</label>
                    
                    <input id={`${a11yId}-gp-phone`} style={styles.input} value={s1.gp_phone}
                      onChange={e => setS1({...s1, gp_phone: e.target.value})}
                      placeholder="+44..." />
                  </div>
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor={`${a11yId}-gp-address`}>GP Address</label>
                  
                  <input id={`${a11yId}-gp-address`} style={styles.input} value={s1.gp_address}
                    onChange={e => setS1({...s1, gp_address: e.target.value})}
                    placeholder="Surgery address" />
                </div>
              </div>
            </div>
          </div>

          {/* ── 5. FAMILY INFORMATION - collapsible ── */}
          <div style={styles.fieldGroup}>
            {/* KBDACCESS: same fix as the Medical header above, same section toggled. */}
            <div style={styles.collapsibleHeader} onClick={() => setFamilyOpen(o => !o)}
              aria-expanded={familyOpen}
              {...activateProps(() => setFamilyOpen(o => !o), { label: 'Family Information' })}>
              <p style={{ ...styles.subBlockLabel, margin: 0 }}>Family Information</p>
              <span style={{ ...styles.chevron, transform: familyOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
            </div>
            {/* KBDACCESS: same fix as the Medical body above - `inert` (with `visibility: hidden`
                as the fallback for browsers without inert support) takes the clipped fields out
                of the tab order while collapsed, so nobody can type into a field they cannot see.
                Applied ONLY while collapsed, so the open section is completely unaffected. */}
            <div style={{ overflow: 'hidden', maxHeight: familyOpen ? '2000px' : '0', transition: 'max-height 0.3s ease', visibility: familyOpen ? 'visible' : 'hidden' }}
              {...(familyOpen ? {} : { inert: true })}>
              <div style={{ paddingTop: '16px' }}>
                {/* Partner */}
                <p style={styles.groupSubtitle}>Partner</p>
                <div style={rowStyle}>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-first-name`}>First Name</label>
                    
                    <input id={`${a11yId}-first-name`} style={styles.input} value={family.partner.first_name}
                      onChange={e => setFamily(f => ({ ...f, partner: { ...f.partner, first_name: e.target.value } }))}
                      placeholder="First name" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-last-name`}>Last Name</label>
                    
                    <input id={`${a11yId}-last-name`} style={styles.input} value={family.partner.last_name}
                      onChange={e => setFamily(f => ({ ...f, partner: { ...f.partner, last_name: e.target.value } }))}
                      placeholder="Last name" />
                  </div>
                </div>
                <div style={{ ...styles.field, maxWidth: '240px', marginBottom: '24px' }}>
                  <label style={styles.label} htmlFor={`${a11yId}-date-of-birth-2`}>Date of Birth</label>
                  
                  <input id={`${a11yId}-date-of-birth-2`} style={styles.input} type="date" value={family.partner.date_of_birth}
                    onChange={e => setFamily(f => ({ ...f, partner: { ...f.partner, date_of_birth: e.target.value } }))} />
                </div>

                {/* Children */}
                <p style={styles.groupSubtitle}>Children</p>
                {family.children.map((child, idx) => (
                  <div key={idx} style={{ borderLeft: '3px solid #eee', paddingLeft: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', color: '#555', fontWeight: '500' }}>Child {idx + 1}</span>
                      <button
                        style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '13px', cursor: 'pointer', padding: '0' }}
                        onClick={() => setFamily(f => ({ ...f, children: f.children.filter((_, i) => i !== idx) }))}>
                        Remove
                      </button>
                    </div>
                    <div style={rowStyle}>
                      <div style={styles.field}>
                        <label style={styles.label} htmlFor={`${a11yId}-first-name-2-${idx}`}>{docText('profileFields.child_first_name', currentLang())}</label>
                        
                        <input id={`${a11yId}-first-name-2-${idx}`} style={styles.input} value={child.first_name}
                          onChange={e => setFamily(f => { const c = [...f.children]; c[idx] = { ...c[idx], first_name: e.target.value }; return { ...f, children: c }; })}
                          placeholder="First name" />
                      </div>
                      <div style={styles.field}>
                        <label style={styles.label} htmlFor={`${a11yId}-last-name-2-${idx}`}>{docText('profileFields.child_last_name', currentLang())}</label>
                        
                        <input id={`${a11yId}-last-name-2-${idx}`} style={styles.input} value={child.last_name}
                          onChange={e => setFamily(f => { const c = [...f.children]; c[idx] = { ...c[idx], last_name: e.target.value }; return { ...f, children: c }; })}
                          placeholder="Last name" />
                      </div>
                    </div>
                    <div style={{ ...styles.field, maxWidth: '240px' }}>
                      <label style={styles.label} htmlFor={`${a11yId}-date-of-birth-3-${idx}`}>{docText('profileFields.child_date_of_birth', currentLang())}</label>
                      
                      <input id={`${a11yId}-date-of-birth-3-${idx}`} style={styles.input} type="date" value={child.date_of_birth}
                        onChange={e => setFamily(f => { const c = [...f.children]; c[idx] = { ...c[idx], date_of_birth: e.target.value }; return { ...f, children: c }; })} />
                    </div>
                  </div>
                ))}
                {family.children.length < 3 && (
                  <button
                    style={{ background: 'none', border: '1px dashed #bbb', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', color: '#555', cursor: 'pointer', width: '100%' }}
                    onClick={() => setFamily(f => ({ ...f, children: [...f.children, { first_name: '', last_name: '', date_of_birth: '' }] }))}>
                    + Add child
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── 6. ADDITIONAL REQUIREMENTS ── */}
          <div style={styles.fieldGroup}>
            <p style={styles.subBlockLabel}>Additional Requirements</p>
            <textarea style={styles.textarea} rows={3} value={s1.additional_requirements}
              onChange={e => setS1({...s1, additional_requirements: e.target.value})}
              placeholder="Any specific requirements or notes (optional)" />
          </div>

          {/* ── Acknowledgements ── */}
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Acknowledgements</h3>
            <label style={styles.checkboxItem}>
              <input type="checkbox" checked={s1.edm_acknowledged}
                onChange={e => setS1({...s1, edm_acknowledged: e.target.checked})} />
              I understand that Enko Capital may remotely remove Outlook and Teams from my personal mobile device if I leave the company.
            </label>
          </div>

          {/* ── Signature & Photo ── */}
          <div style={{ ...styles.fieldGroup, borderBottom: 'none' }}>
            <h3 style={styles.groupTitle}>Signature & Photo</h3>
            <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px 0' }}>JPEG or PNG only. Photo is required; signature is optional. Both will be stored in your employee profile.</p>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>

              {/* Signature */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={styles.label}>Signature sample</label>
                {employeeRaw?.signature_url && (
                  (
                    // DLNAME: View and Download as a pair, wrapped in a fragment because the
                    // branch they sit in takes ONE expression.
                    <>
                      <button style={uploadZoneStyles.viewBtn} onClick={() => viewS1File('signature')}>
                      View current signature
                      </button>
                      {/* DLNAME: download under the readable server name. */}
                      <button style={uploadZoneStyles.viewBtn} onClick={() => downloadS1File('signature')}>Download</button>
                    </>
                  )
                )}
                {!employeeRaw?.signature_url && formData?.signature_temp_url && (
                  <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Staged: {formData.signature_temp_url.split('/').pop()}
                    <button
                      type="button"
                      onClick={() => clearStagedFile('signature')}
                      style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}
                      title="Remove staged file"
                    >×</button>
                  </p>
                )}
                <div
                  style={{ ...uploadZoneStyles.zone, ...(sigDragOver ? uploadZoneStyles.zoneActive : {}) }}
                  onClick={() => document.getElementById('s1-sig-input').click()}
                  onDragOver={e => { e.preventDefault(); setSigDragOver(true); }}
                  onDragEnter={e => { e.preventDefault(); setSigDragOver(true); }}
                  onDragLeave={() => setSigDragOver(false)}
                  onDrop={e => { e.preventDefault(); setSigDragOver(false); const f = e.dataTransfer.files[0]; if (f) setSignatureFile(f); }}
                >
                  {signatureFile
                    ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <span style={uploadZoneStyles.fileName}>{signatureFile.name}</span>
                        <button
                          type="button"
                          onClick={e => clearLocalFile(e, setSignatureFile, 's1-sig-input')}
                          style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}
                          title="Remove selected file"
                        >×</button>
                      </span>
                    )
                    : <span style={uploadZoneStyles.placeholder}>Click or drag & drop (JPEG / PNG)</span>}
                </div>
                <input id="s1-sig-input" type="file" accept=".jpg,.jpeg,.png" style={{ display: 'none' }}
                  onChange={e => setSignatureFile(e.target.files[0])} />
                {signatureFile && (
                  <button
                    style={uploadZoneStyles.uploadBtn}
                    disabled={uploadingSig}
                    onClick={() => uploadProfileFile('signature', signatureFile, setSignatureFile, setUploadingSig)}
                  >
                    {uploadingSig ? 'Uploading…' : 'Upload Signature'}
                  </button>
                )}
              </div>

              {/* Photo */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={styles.label}>Photo <RequiredMark /></label>
                {employeeRaw?.photo_url && (
                  (
                    // DLNAME: View and Download as a pair, wrapped in a fragment because the
                    // branch they sit in takes ONE expression.
                    <>
                      <button style={uploadZoneStyles.viewBtn} onClick={() => viewS1File('photo')}>
                      View current photo
                      </button>
                      {/* DLNAME: download under the readable server name. */}
                      <button style={uploadZoneStyles.viewBtn} onClick={() => downloadS1File('photo')}>Download</button>
                    </>
                  )
                )}
                {!employeeRaw?.photo_url && formData?.photo_temp_url && (
                  <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Staged: {formData.photo_temp_url.split('/').pop()}
                    <button
                      type="button"
                      onClick={() => clearStagedFile('photo')}
                      style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}
                      title="Remove staged file"
                    >×</button>
                  </p>
                )}
                <div
                  style={{ ...uploadZoneStyles.zone, ...(photoDragOver ? uploadZoneStyles.zoneActive : {}) }}
                  onClick={() => document.getElementById('s1-photo-input').click()}
                  onDragOver={e => { e.preventDefault(); setPhotoDragOver(true); }}
                  onDragEnter={e => { e.preventDefault(); setPhotoDragOver(true); }}
                  onDragLeave={() => setPhotoDragOver(false)}
                  onDrop={e => { e.preventDefault(); setPhotoDragOver(false); const f = e.dataTransfer.files[0]; if (f) setPhotoFile(f); }}
                >
                  {photoFile
                    ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <span style={uploadZoneStyles.fileName}>{photoFile.name}</span>
                        <button
                          type="button"
                          onClick={e => clearLocalFile(e, setPhotoFile, 's1-photo-input')}
                          style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}
                          title="Remove selected file"
                        >×</button>
                      </span>
                    )
                    : <span style={uploadZoneStyles.placeholder}>Click or drag & drop (JPEG / PNG)</span>}
                </div>
                <input id="s1-photo-input" type="file" accept=".jpg,.jpeg,.png" style={{ display: 'none' }}
                  onChange={e => setPhotoFile(e.target.files[0])} />
                {photoFile && (
                  <button
                    style={uploadZoneStyles.uploadBtn}
                    disabled={uploadingPhoto}
                    onClick={() => uploadProfileFile('photo', photoFile, setPhotoFile, setUploadingPhoto)}
                  >
                    {uploadingPhoto ? 'Uploading…' : 'Upload Photo'}
                  </button>
                )}
              </div>

            </div>
          </div>
          </>
          )}

          {/* ── PART B: FINANCIAL & TAX DETAILS ── */}
          {s1Step === 'financial' && (
          <>
          {/* ── 2. BANK DETAILS ── */}
          <div style={styles.fieldGroup}>
            <p style={styles.subBlockLabel}>Bank Details (for payroll)</p>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-account-holder-name`}>Account Holder Name</label>
              
              <input id={`${a11yId}-account-holder-name`} style={styles.input} value={s1.bank_account_name}
                onChange={e => setS1({...s1, bank_account_name: e.target.value})}
                placeholder="Name exactly as it appears on the account" />
            </div>
            {isUK ? (
              <>
                <div style={rowStyle}>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-bank-name`}>Bank Name <RequiredMark /></label>
                    
                    <input id={`${a11yId}-bank-name`} style={styles.input} value={s1.rib_bank_name}
                      onChange={e => setS1({...s1, rib_bank_name: e.target.value})}
                      placeholder="e.g. Barclays" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-sort-code`}>Sort Code <RequiredMark /></label>
                    
                    <input id={`${a11yId}-sort-code`} style={styles.input} value={s1.rib_sort_code}
                      onChange={e => setS1({...s1, rib_sort_code: e.target.value})}
                      placeholder="e.g. 20-00-00" />
                  </div>
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor={`${a11yId}-account-number`}>Account Number <RequiredMark /></label>
                  
                  <input id={`${a11yId}-account-number`} style={styles.input} value={s1.rib_iban}
                    onChange={e => setS1({...s1, rib_iban: e.target.value})}
                    placeholder="e.g. 12345678" />
                </div>
              </>
            ) : (
              <>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor={`${a11yId}-iban`}>IBAN <RequiredMark /></label>
                  
                  <input id={`${a11yId}-iban`} style={styles.input} value={s1.rib_iban}
                    onChange={e => setS1({...s1, rib_iban: e.target.value})}
                    placeholder="e.g. FR7630006000011234567890189" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor={`${a11yId}-swift-bic`}>SWIFT / BIC <RequiredMark /></label>
                  
                  <input id={`${a11yId}-swift-bic`} style={styles.input} value={s1.rib_sort_code}
                    onChange={e => setS1({...s1, rib_sort_code: e.target.value})}
                    placeholder="e.g. BNPAFRPP" />
                </div>
              </>
            )}
          </div>

          {/* TAXTOS1 (Phase 1 of 3): these two groups moved here from Section 3. They are the
              employee's own personal facts, and Section 1 is the employee-facing section.
              Title/Marital Status render unconditionally (as they did in Section 3); the UK
              Tax and HMRC group keeps its isUK gate unchanged. */}
          {/* TAXTOS1 (Phase 1) moved these two groups here from Section 3. TAXFIELDS (Phase 2)
              then extracted them into StarterTaxFields so the identical fields can also be
              rendered by EmployeeView.js's "I don't have a P45" flow, with no duplication.
              Rendering and behaviour here are unchanged: same fields, same isUK gate, same
              unconditional Title/Marital Status. */}
          <StarterTaxFields
            value={s1}
            set={patch => setS1({ ...s1, ...patch })}
            styles={styles}
            showUKTax={isUK}
            RequiredMark={RequiredMark}
          />
          </>
          )}

          <div style={styles.btnRow}>
            {onBack && (
              <button
                style={styles.backBtnRow}
                onClick={onBack}
                onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
              >
                ← Back to dashboard
              </button>
            )}
            {/* S1SPLIT: per-step actions. Part A (personal) saves via ?part=a (submitPartA runs
                the EDM + photo gates that belong to this step) and advances to Financial & Tax on
                success. Part B (financial) offers Back to personal and the final submit via ?part=b
                (submitPartB, which calls onComplete). Save Draft (unchanged) is available on both. */}
            {s1Step === 'personal' ? (
              <>
                <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                  {saving ? 'Saving...' : 'Save draft'}
                </button>
                <button style={styles.submitBtn} onClick={submitPartA} disabled={loading || saving || uploadingPhoto || uploadingSig || !formData || !employeeRaw}>
                  {loading ? <><Spinner />Saving...</> : 'Save & Continue to Financial & Tax'}
                </button>
              </>
            ) : (
              <>
                <button style={styles.saveBtn} onClick={() => setS1Step('personal')} disabled={saving || loading}>
                  Back
                </button>
                <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                  {saving ? 'Saving...' : 'Save draft'}
                </button>
                <button style={styles.submitBtn} onClick={submitPartB} disabled={loading || saving || !formData || !employeeRaw}>
                  {loading ? <><Spinner />Saving...</> : 'Submit'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── SECTION 2 - RFA ─── */}
      {/* HRS3 (confirmed COO decision): HR never sees or fills the IT section. The server refuses
          the write and withholds the fields from hr; this is the screen-side half, so an hr
          account that reaches section 2 by any route sees a refusal rather than an empty form. */}
      {section === 2 && currentUserRole === 'hr' && (
        <div style={styles.accessDenied}>
          <p style={styles.accessDeniedTitle}>{t('newStarter.itSectionRestrictedTitle')}</p>
          <p style={styles.accessDeniedMsg}>{t('newStarter.itSectionRestrictedMsg')}</p>
        </div>
      )}
      {section === 2 && currentUserRole !== 'hr' && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Section 2: IT Setup</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {formData?.section2_completed && <span style={styles.submittedBadge}>✓ Submitted</span>}
              {!section2Locked && isTestAccount() && <button style={styles.demoBtn} onClick={() => setS2(DEMO_S2)}>Fill demo data</button>}
            </div>
          </div>

          {section2Locked && (
            <div style={{ background: '#fffbea', border: '1px solid #d4ac0d', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', color: '#7d6608', fontSize: '14px' }}>
              IT Setup has been submitted and the notification sent. This section is now permanently read-only.
            </div>
          )}

          <div style={section2Locked ? { pointerEvents: 'none', opacity: 0.72 } : {}}>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Device & Security</h3>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.intune_enrollment}
                  onChange={e => setS2({...s2, intune_enrollment: e.target.checked})} />
                InTune Enrollment
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.mfa_enrollment}
                  onChange={e => setS2({...s2, mfa_enrollment: e.target.checked})} />
                MFA Authenticator Enrollment
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.edm_mobile}
                  onChange={e => setS2({...s2, edm_mobile: e.target.checked})} />
                EDM Outlook and Teams on personal mobile
              </label>
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Licences</h3>
            {/* LICNOTE: the "add/purchase a licence if not available" note was removed from this
                UI deliberately. It is an instruction for RFA (who action the licences), not for
                the person filling this form, and it now lives in the Excel attachment instead
                (new_starter.py's Licences block). Do not reinstate it here. */}
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={Boolean(s2.ms365_licence)}
                  onChange={e => setS2({...s2, ms365_licence: e.target.checked ? 'Microsoft 365 E3' : ''})} />
                Microsoft 365 E3
              </label>

              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.copilot_licence}
                  onChange={e => setS2({...s2, copilot_licence: e.target.checked})} />
                Copilot Licence
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.powerbi_licence}
                  onChange={e => setS2({...s2, powerbi_licence: e.target.checked})} />
                Power BI Pro Licence
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.defender_cloud_apps}
                  onChange={e => setS2({...s2, defender_cloud_apps: e.target.checked})} />
                Microsoft Defender for Cloud Apps
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.e5_information_protection}
                  onChange={e => setS2({...s2, e5_information_protection: e.target.checked})} />
                Microsoft 365 E5 Information Protection and Governance
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.teams_domestic}
                  onChange={e => setS2({...s2, teams_domestic: e.target.checked})} />
                Microsoft Teams for Telephone Calls (includes international calling plan)
              </label>
            </div>
            {/* LICAPPROVE: explicit Yes/No approval, replacing the static instructional note that
                used to sit at the top of this group. Checked by default - the normal answer is
                yes - so RFA receives a recorded decision rather than only a standing instruction. */}
            <div style={{ ...styles.field, marginTop: '16px' }}>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.approve_licence_purchase}
                  onChange={e => setS2({...s2, approve_licence_purchase: e.target.checked})} />
                Approve purchasing additional licence seats if needed
              </label>
              <p style={styles.hint}>
                Leave ticked to authorise RFA to buy a licence when one is not already available.
                Untick to require a separate approval first.
              </p>
            </div>

            {/* ADDLIC: free-text request for a licence not covered by the fixed checkboxes above.
                Flows through to its own dedicated row in the Excel sent to RFA. */}
            <div style={{ ...styles.field, marginTop: '16px' }}>
              <label style={styles.label} htmlFor={`${a11yId}-additional-licence-requirements-if-n`}>Additional licence requirements (if not listed above)</label>
              
              <textarea id={`${a11yId}-additional-licence-requirements-if-n`} style={styles.textarea} rows={3} value={s2.additional_licences}
                onChange={e => setS2({...s2, additional_licences: e.target.value})}
                placeholder="e.g. Adobe Acrobat Pro, Visio, or any third party tool licence" />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Calendar & Teams</h3>
            <div style={styles.field}>
              <label style={styles.label}>Should this employee receive calendar permissions to the 3 shared calendars for permanent staff (Holiday@enkocapital.com, WFH@enkocapital.com, SickLeave@enkocapital.com)?</label>
              <div style={{...styles.radioGroup, marginTop: '8px'}}>
                {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                  <label key={lbl} style={styles.checkboxItem}>
                    <input type="radio" name="shared_calendar_permissions" value={lbl}
                      checked={s2.shared_calendar_permissions === val}
                      onChange={() => setS2({...s2, shared_calendar_permissions: val})} />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-teams-call-recording-group`}>Teams Call Recording Group</label>
              
              <select id={`${a11yId}-teams-call-recording-group`} style={{...styles.input, maxWidth: '420px'}} value={s2.teams_call_recording_group}
                onChange={e => setS2({...s2, teams_call_recording_group: e.target.value})}>
                <option value="">None</option>
                <option value="TeamsCallRecording">TeamsCallRecording (persistent)</option>
                <option value="TeamsCallRecordingOnDemand">TeamsCallRecordingOnDemand (on-demand, requires CallCabinet Atmos Recording extension)</option>
              </select>
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>SharePoint Access</h3>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-sites`}>Sites</label>
              
              <select id={`${a11yId}-sites`}
                multiple
                style={{ ...styles.input, height: '192px', cursor: 'pointer', padding: '4px' }}
                value={s2.sharepoint_sites}
                onChange={e => setS2({ ...s2, sharepoint_sites: Array.from(e.target.selectedOptions).map(o => o.value) })}
              >
                {SHAREPOINT_SITES.map(site => (
                  <option key={site.url} value={site.url}>{site.label}</option>
                ))}
              </select>
              <p style={styles.hint}>Hold Ctrl to select multiple sites.</p>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Distribution Groups</label>
              <div style={styles.checkboxGroup}>
                {DISTRIBUTION_GROUPS.map(group => (
                  <label key={group} style={styles.checkboxItem}>
                    <input type="checkbox"
                      checked={s2.distribution_groups.includes(group)}
                      onChange={e => setS2({
                        ...s2,
                        distribution_groups: e.target.checked
                          ? [...s2.distribution_groups, group]
                          : s2.distribution_groups.filter(g => g !== group)
                      })} />
                    {group}
                  </label>
                ))}
              </div>
            </div>
          </div>

          </div>

          {!section2Locked && (
            <div style={styles.btnRow}>
              {onBack && (
                <button
                  style={styles.backBtnRow}
                  onClick={onBack}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
                >
                  ← Back to dashboard
                </button>
              )}
              <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                {saving ? 'Saving...' : 'Save draft'}
              </button>
              {/* S2CONFIRM: Submit now opens the review modal instead of calling submit() directly.
                  The modal's "Confirm & Submit" is the only path to the real submit. */}
              <button style={styles.submitBtn} onClick={() => setShowS2Confirm(true)} disabled={loading || saving || uploadingPhoto || uploadingSig || !formData || !employeeRaw}>
                {loading ? <><Spinner />{formData?.section2_completed ? 'Updating...' : 'Submitting...'}</> : formData?.section2_completed ? 'Review & Update' : 'Review & Submit'}
              </button>
            </div>
          )}

          {/* S2CONFIRM: the review-and-confirm summary modal. Lists EXACTLY what is selected in
              Section 2 so an accidental tick (the IM Group dispute) is caught before the IT Setup
              xlsx/email reaches RFA. "Go back and edit" just closes it (s2 state is untouched, so
              nothing is lost); "Confirm & Submit" closes it and fires the EXISTING submit(). */}
          {showS2Confirm && (
            <Section2ConfirmModal
              s2={s2}
              isUpdate={!!formData?.section2_completed}
              onCancel={() => setShowS2Confirm(false)}
              onConfirm={() => { setShowS2Confirm(false); submit(); }}
            />
          )}
        </div>
      )}

      {/* ─── SECTION 3 - HR ─── (SASCOPE: never for compliance) */}
      {section === 3 && !hidesSection3 && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>{t('newStarterS3.title', 'Section 3: Payroll and Benefits')}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {formData?.section3_completed && <span style={styles.submittedBadge}>{t('newStarterS3.submitted', '✓ Submitted')}</span>}
              {!section3Locked && isTestAccount() && <button style={styles.demoBtn} onClick={() => setS3(DEMO_S3)}>{t('newStarterS3.fillDemo', 'Fill demo data')}</button>}
            </div>
          </div>

          {cooLocked && (
            <div style={{ background: '#fffbea', border: '1px solid #d4ac0d', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', color: '#7d6608', fontSize: '14px' }}>
              {t('newStarterS3.lockedBoth', 'Both sections (IT Setup and Payroll) have been submitted. This section is now read-only.')}
            </div>
          )}

          {/* COOREADALL: read-but-not-write. Shown only when the viewer genuinely lacks write
              access, and not duplicated when cooLocked already explains the read-only state. */}
          {section3ReadOnly && !cooLocked && (
            <div style={{ background: '#fffbea', border: '1px solid #d4ac0d', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', color: '#7d6608', fontSize: '14px' }}>
              {t('newStarterS3.readOnlyOther', "You can view this section, but payroll for this employee's country is handled by a different payroll contact, so it is read-only for you.")}
            </div>
          )}

          <div style={section3Locked ? { pointerEvents: 'none', opacity: 0.72 } : {}}>
          {/* ── Employment Type ── */}
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('newStarterS3.employmentType', 'Employment Type')}</h3>
            <div style={styles.radioGroup}>
              {['Full-time', 'Part-time'].map(opt => (
                <label key={opt} style={styles.checkboxItem}>
                  <input type="radio" name="employment_type" value={opt}
                    checked={s3.employment_type === opt}
                    onChange={() => setS3({...s3, employment_type: opt, work_pattern: '', contracted_hours: ''})} />
                  {opt === 'Full-time' ? t('newStarterS3.fullTime', 'Full-time') : t('newStarterS3.partTime', 'Part-time')}
                </label>
              ))}
            </div>
            {s3.employment_type === 'Part-time' && (
              <div style={{marginTop: '16px', ...styles.row}}>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor={`${a11yId}-work-pattern`}>{t('newStarterS3.workPattern', 'Work Pattern')}</label>
                  
                  <input id={`${a11yId}-work-pattern`} style={styles.input} value={s3.work_pattern}
                    onChange={e => setS3({...s3, work_pattern: e.target.value})}
                    placeholder={t('newStarterS3.workPatternPlaceholder', 'e.g. Mon to Wed, 9am to 5pm')} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor={`${a11yId}-contracted-hours-per-week`}>{t('newStarterS3.contractedHours', 'Contracted Hours Per Week')}</label>
                  
                  <input id={`${a11yId}-contracted-hours-per-week`} style={styles.input} type="number" min="1" max="40" value={s3.contracted_hours}
                    onChange={e => setS3({...s3, contracted_hours: e.target.value})}
                    placeholder={t('newStarterS3.contractedHoursPlaceholder', 'e.g. 21')} />
                </div>
              </div>
            )}
          </div>

          {/* ── Directorship ── */}
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('newStarterS3.directorship', 'Directorship')}</h3>
            <label style={styles.label}>{t('newStarterS3.isDirector', 'Is the employee a Director of this company?')}</label>
            <div style={{...styles.radioGroup, marginTop: '8px'}}>
              {[['Yes', true], ['No', false]].map(([label, val]) => (
                <label key={label} style={styles.checkboxItem}>
                  <input type="radio" name="is_director" value={label}
                    checked={s3.is_director === val}
                    onChange={() => setS3({...s3, is_director: val, directorship_start_date: ''})} />
                  {val ? t('newStarterS3.yes', 'Yes') : t('newStarterS3.no', 'No')}
                </label>
              ))}
            </div>
            {s3.is_director && (
              <div style={{...styles.field, marginTop: '16px', maxWidth: '240px'}}>
                <label style={styles.label} htmlFor={`${a11yId}-directorship-start-date`}>{t('newStarterS3.directorshipStart', 'Directorship Start Date')} <RequiredMark /></label>
                
                <input id={`${a11yId}-directorship-start-date`} style={styles.input} type="date" value={s3.directorship_start_date}
                  onChange={e => setS3({...s3, directorship_start_date: e.target.value})} />
              </div>
            )}
          </div>

          {/* ── Compensation and Working Conditions ── */}
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('newStarterS3.compensation', 'Compensation and Working Conditions')}</h3>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-currency`}>{t('newStarterS3.currency', 'Currency')} <RequiredMark /></label>
              
              <select id={`${a11yId}-currency`} style={styles.input} value={s3.currency}
                onChange={e => setS3({...s3, currency: e.target.value})}>
                <option value="">{t('newStarterS3.selectCurrency', 'Select currency')}</option>
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{t(`newStarterS3.currencies.${c.code}`, c.label)}</option>
                ))}
              </select>
            </div>
            <div style={rowStyle}>
              <div style={styles.field}>
                {/* SALARYPERIOD: label is now plain "Salary" - the period is explicit in the
                    control beside it, so the label must not assert one. */}
                <label style={styles.label} htmlFor={`${a11yId}-salary`}>{t('newStarterS3.salary', 'Salary')} <RequiredMark /></label>
                
                <input id={`${a11yId}-salary`} style={styles.input} type="number" value={s3.salary}
                  onChange={e => setS3({...s3, salary: e.target.value})}
                  placeholder={t('newStarterS3.salaryPlaceholder', 'e.g. 85000')} />
              </div>
              <div style={styles.field}>
                {/* SALARYPERIOD: defaulted per entity (annual for the UK, monthly elsewhere) via
                    ENTITY_PAYROLL_CONFIG.defaultSalaryPeriod, and freely overridable here. */}
                <label style={styles.label} htmlFor={`${a11yId}-salary-period`}>{t('newStarterS3.salaryPeriod', 'Salary Period')} <RequiredMark /></label>
                
                <select id={`${a11yId}-salary-period`} style={styles.input} value={s3.salary_period}
                  onChange={e => setS3({...s3, salary_period: e.target.value})}>
                  <option value="monthly">{t('newStarterS3.monthly', 'Monthly')}</option>
                  <option value="annual">{t('newStarterS3.annual', 'Annual')}</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-working-days-per-week`}>{t('newStarterS3.workingDays', 'Working Days Per Week')} <RequiredMark /></label>
                
                <select id={`${a11yId}-working-days-per-week`} style={styles.input} value={s3.working_days_per_week}
                  onChange={e => setS3({...s3, working_days_per_week: parseInt(e.target.value)})}>
                  {[3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} {t('newStarterS3.days', 'days')}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ── Payroll Setup ── */}
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>{t('newStarterS3.payrollSetup', 'Payroll Setup')}</h3>
            {showPension && (
              <div style={{...styles.fieldGroup, border: 'none', marginBottom: '0', paddingBottom: '0'}}>
                <p style={styles.groupSubtitle}>{t('newStarterS3.pension', 'Pension')}</p>
                <label style={styles.checkboxItem}>
                  <input type="checkbox" checked={s3.pension_enrolled}
                    onChange={e => setS3({...s3, pension_enrolled: e.target.checked})} />
                  {t('newStarterS3.pensionEnrolled', 'Enrolled in pension scheme at statutory rate')}
                </label>
                {/* PENSIONWORD: the statutory rate is set by regulation on a qualifying-earnings
                    band and can change, so no fixed percentage is captured or displayed here. The
                    provider/contribution-rate fields were removed for the same reason. */}
                <p style={styles.hint}>{t('newStarterS3.pensionHint', 'Employee must be auto-enrolled at the statutory rate. They may opt out separately.')}</p>
              </div>
            )}
            {showHealthInsurance && (
              <div style={{marginTop: showPension ? '20px' : '0'}}>
                <p style={styles.groupSubtitle}>{t('newStarterS3.healthInsurance', 'Health Insurance')}</p>
                <label style={styles.checkboxItem}>
                  <input type="checkbox" checked={s3.health_insurance_eligible}
                    onChange={e => setS3({...s3, health_insurance_eligible: e.target.checked})} />
                  {t('newStarterS3.healthEligible', 'Eligible for health insurance')}
                </label>
                {/* HEALTHINS: eligibility flag only. Not everyone is eligible (e.g. short-term
                    contract staff), so the COO/admin ticks this from the contract terms. Enrolment
                    is arranged separately by the Financial Controller outside the portal, so no
                    enrolment date is stored (people opt in/out over time), and the provider is
                    reviewed annually so it is not hardcoded per employee. */}
                <p style={styles.hint}>{t('newStarterS3.healthHint', 'Flags that this person is eligible. The Financial Controller arranges enrolment separately.')}</p>
              </div>
            )}
          </div>

          {/* ALLOWANCES: repeating lines with add/remove, following the employee_family pattern.
              The whole group renders ONLY when this employee's country offers allowance types
              (read from the server), so no country sees an empty, meaningless section.
              ALLOWPOS (confirmed decision by Marie-Lynne): this group sits AFTER Payroll Setup, so
              it reads below Health Insurance, which is that group's last field. Moved verbatim -
              the render condition, the payload building and the exclude_unset gating are all
              untouched; only the position in the markup changed. It stays its OWN fieldGroup
              sibling rather than being nested inside Payroll Setup, which would have put a
              fieldGroup inside a fieldGroup and changed its framing. */}
          {allowanceTypes.length > 0 && (
            <div style={styles.fieldGroup}>
              <h3 style={styles.groupTitle}>{t('newStarterS3.allowances', 'Allowances')}</h3>
              {allowances.length === 0 && (
                <p style={{ fontSize: '13px', color: '#888', margin: '0 0 12px 0' }}>
                  {t('newStarterS3.noAllowances', 'No allowances recorded.')}
                </p>
              )}
              {allowances.map((alw, idx) => (
                <div key={idx} style={{ borderLeft: '3px solid #eee', paddingLeft: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#555', fontWeight: '500' }}>{t('newStarterS3.allowance', 'Allowance')} {idx + 1}</span>
                    <button
                      style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '13px', cursor: 'pointer', padding: '0' }}
                      onClick={() => setAllowances(list => list.filter((_, i) => i !== idx))}>
                      {t('newStarterS3.remove', 'Remove')}
                    </button>
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-type-${idx}`}>{t('newStarterS3.type', 'Type')}</label>
                    
                    <select id={`${a11yId}-type-${idx}`} style={styles.input} value={alw.allowance_type}
                      onChange={e => setAllowances(list => { const c = [...list]; c[idx] = { ...c[idx], allowance_type: e.target.value }; return c; })}>
                      {allowanceTypes.map(t => (
                        <option key={t.value} value={t.value}>{s3T(`newStarterS3.allowanceTypes.${t.value}`, t.label)}</option>
                      ))}
                    </select>
                  </div>
                  <div style={rowStyle}>
                    <div style={styles.field}>
                      <label style={styles.label} htmlFor={`${a11yId}-amount-${idx}`}>{t('newStarterS3.amount', 'Amount')}</label>
                      
                      <input id={`${a11yId}-amount-${idx}`} style={styles.input} type="number" value={alw.amount}
                        onChange={e => setAllowances(list => { const c = [...list]; c[idx] = { ...c[idx], amount: e.target.value }; return c; })}
                        placeholder={t('newStarterS3.amountPlaceholder', 'e.g. 50000')} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label} htmlFor={`${a11yId}-currency-2-${idx}`}>{t('newStarterS3.currency', 'Currency')}</label>
                      
                      <select id={`${a11yId}-currency-2-${idx}`} style={styles.input} value={alw.currency}
                        onChange={e => setAllowances(list => { const c = [...list]; c[idx] = { ...c[idx], currency: e.target.value }; return c; })}>
                        <option value="">{t('newStarterS3.selectCurrency', 'Select currency')}</option>
                        {CURRENCIES.map(c => (
                          <option key={c.code} value={c.code}>{t(`newStarterS3.currencies.${c.code}`, c.label)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ ...styles.field, maxWidth: '240px' }}>
                    <label style={styles.label} htmlFor={`${a11yId}-period-${idx}`}>{t('newStarterS3.period', 'Period')}</label>
                    
                    <select id={`${a11yId}-period-${idx}`} style={styles.input} value={alw.period}
                      onChange={e => setAllowances(list => { const c = [...list]; c[idx] = { ...c[idx], period: e.target.value }; return c; })}>
                      <option value="monthly">{t('newStarterS3.monthly', 'Monthly')}</option>
                      <option value="annual">{t('newStarterS3.annual', 'Annual')}</option>
                    </select>
                  </div>
                </div>
              ))}
              <button
                style={{ ...styles.demoBtn, marginTop: '4px' }}
                onClick={() => setAllowances(list => [...list, {
                  allowance_type: allowanceTypes[0]?.value || '',
                  amount: '', currency: s3.currency || '', period: s3.salary_period || 'monthly',
                }])}>
                {t('newStarterS3.addAllowance', '+ Add allowance')}
              </button>
            </div>
          )}

          </div>

          {/* COOREADALL: no Save draft / Submit control at all when the viewer cannot write -
              the backend would reject it, so it is not offered. */}
          {!section3Locked && (
            <div style={styles.btnRow}>
              {onBack && (
                <button
                  style={styles.backBtnRow}
                  onClick={onBack}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
                >
                  {t('newStarterS3.backToDashboard', '← Back to dashboard')}
                </button>
              )}
              <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                {saving ? t('newStarterS3.saving', 'Saving...') : t('newStarterS3.saveDraft', 'Save draft')}
              </button>
              <button style={styles.submitBtn} onClick={submit} disabled={loading || saving || uploadingPhoto || uploadingSig || !formData || !employeeRaw}>
                {loading ? <><Spinner />{formData?.section3_completed ? t('newStarterS3.updating', 'Updating...') : t('newStarterS3.submitting', 'Submitting...')}</> : formData?.section3_completed ? t('newStarterS3.update', 'Update') : t('newStarterS3.submit', 'Submit')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── SECTION 4 - Compliance (4A) and HR (4B) ─── */}
      {/* TEMPORARY: 'coo' added alongside compliance/admin so the COO can fully cover Section 4
          until a dedicated Compliance hire is in place. S4AB: 'hr' is admitted for Section 4B only. */}
      {section === 4 && (
        !['compliance', 'admin', 'coo', 'hr'].includes(currentUserRole)
          ? (
            <div style={styles.accessDenied}>
              <p style={styles.accessDeniedTitle}>Access Restricted</p>
              <p style={styles.accessDeniedMsg}>
                This section is only accessible to Compliance and authorised staff.
              </p>
            </div>
          )
          : (
          <div style={styles.form}>
            {onBack && (
              <button
                style={styles.backBtn}
                onClick={onBack}
                onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
              >
                ← Back to dashboard
              </button>
            )}
            <div style={styles.formHeader}>
              <h2 style={styles.formTitle}>Section 4: Compliance</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {formData?.section4_completed && <span style={styles.submittedBadge}>✓ Submitted</span>}
                {isTestAccount() && canEdit4a && <button style={styles.demoBtn} onClick={() => setS4(DEMO_S4)}>Fill demo data</button>}
              </div>
            </div>

            {/* S4AB: 4A then 4B. S4CLASSHIDE kept: for a migrated employee, or where a background
                check is not legal, there is no background check step, so only 4A is shown. HR
                cannot open 4A; everyone else may look at either step. */}
            <div style={styles.s4Steps}>
              {(isClassificationOnly
                ? [['classification', s4t('section4ab.title4a')]]
                : [['classification', s4t('section4ab.title4a')], ['4b', s4t('section4ab.title4b')]]
              ).map(([key, label], idx) => {
                const isCurrent = s4Step === key;
                const isDone = key === 'classification' ? !!formData?.section4a_completed : !!formData?.section4b_completed;
                const canGo = !isCurrent && (key === '4b' || canEdit4a);
                return (
                  <div key={key} data-testid={`s4-step-${key}`}
                    style={{ ...styles.s4StepItem, cursor: canGo ? 'pointer' : 'default' }}
                    onClick={() => { if (canGo) { setError(null); setS4Step(key); } }}>
                    <div style={{ ...styles.s4StepDot, backgroundColor: isCurrent ? NAVY_FORM : isDone ? '#27ae60' : '#ddd' }}>
                      {isDone && !isCurrent ? '✓' : idx + 1}
                    </div>
                    <span style={{ ...styles.s4StepLabel, color: isCurrent ? NAVY_FORM : isDone ? '#555' : '#667085' }}>{label}</span>
                  </div>
                );
              })}
            </div>

            {/* S4BGCIV: why there is no background check step, when the reason is the legal one. */}
            {bgCheckNotLegal && (
              <p style={{ ...styles.hint, marginTop: '4px' }} data-testid="s4-bg-not-applicable">
                {t('newStarter.backgroundCheckNotApplicable')}
              </p>
            )}

            {/* ── SECTION 4A: BACKGROUND CHECK CLASSIFICATION (compliance) ── */}
            {s4Step === 'classification' && canEdit4a && (
            <>
            {/* Regulatory classification - Compliance-confirmed (Holly/Craig sign-off). Purely
                additive: classification/tagging only, no certificate upload here - that's the
                separate, future Fitness & Propriety Assessment workflow. */}
            <div style={styles.fieldGroup}>
              <h3 style={styles.groupTitle}>Regulatory Classification</h3>
              <p style={styles.hint}>
                An employee may hold one or more Certified Functions and/or Non-Certified Functions,
                and may separately be classified as Senior Management. A certificate for each
                Certified Function will be requested once the Fitness &amp; Propriety Assessment
                workflow is available.
              </p>

              <div style={{ ...styles.field, marginTop: '12px', opacity: s4.classification_none_confirmed ? 0.5 : 1, pointerEvents: s4.classification_none_confirmed ? 'none' : 'auto' }}>
                <label style={styles.label}>Certified Function(s)</label>
                <div style={styles.checkboxGroup}>
                  {CERTIFIED_FUNCTIONS.map(fn => (
                    <label key={fn} style={styles.checkboxItem}>
                      <input type="checkbox"
                        checked={s4.certified_functions.includes(fn)}
                        onChange={e => setS4({
                          ...s4,
                          certified_functions: e.target.checked
                            ? [...s4.certified_functions, fn]
                            : s4.certified_functions.filter(f => f !== fn),
                          classification_none_confirmed: false,
                        })} />
                      {fn}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ ...styles.field, marginTop: '16px', opacity: s4.classification_none_confirmed ? 0.5 : 1, pointerEvents: s4.classification_none_confirmed ? 'none' : 'auto' }}>
                <label style={styles.label}>Non-Certified Function(s)</label>
                <p style={styles.hint}>
                  Select any regulatory roles this person holds. An individual may hold several
                  roles across several jurisdictions, not limited to their primary entity.
                </p>
                {/* NONCERTLIST: a jurisdiction-grouped multi-select (checkboxes), replacing the old
                    free-text add/remove field. Selecting/deselecting is a plain checkbox toggle,
                    which by construction has no "typed text invisible" issue and no missing-removal
                    issue - unticking a box removes it. Stores the same array as before. */}
                {NON_CERTIFIED_FUNCTION_GROUPS.map(group => (
                  <div key={group.jurisdiction} style={{ marginTop: '10px' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: 600, color: NAVY_FORM }}>
                      {group.jurisdiction}
                    </p>
                    <div style={styles.checkboxGroup}>
                      {group.roles.map(role => {
                        const val = nonCertifiedValue(group.jurisdiction, role);
                        return (
                          <label key={val} style={styles.checkboxItem}>
                            <input type="checkbox"
                              checked={s4.non_certified_functions.includes(val)}
                              onChange={e => setS4({
                                ...s4,
                                non_certified_functions: e.target.checked
                                  ? [...s4.non_certified_functions, val]
                                  : s4.non_certified_functions.filter(f => f !== val),
                                classification_none_confirmed: false,
                              })} />
                            {role}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <label style={{ ...styles.checkboxItem, marginTop: '16px', opacity: s4.classification_none_confirmed ? 0.5 : 1, pointerEvents: s4.classification_none_confirmed ? 'none' : 'auto' }}>
                <input type="checkbox" checked={s4.senior_management}
                  onChange={e => setS4({ ...s4, senior_management: e.target.checked, classification_none_confirmed: false })} />
                Senior Management
              </label>

              <label style={{ ...styles.checkboxItem, marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e5e5e5' }}>
                <input type="checkbox" checked={s4.classification_none_confirmed}
                  onChange={e => setS4({
                    ...s4,
                    classification_none_confirmed: e.target.checked,
                    certified_functions: e.target.checked ? [] : s4.certified_functions,
                    non_certified_functions: e.target.checked ? [] : s4.non_certified_functions,
                    senior_management: e.target.checked ? false : s4.senior_management,
                  })} />
                None of these apply
              </label>
            </div>


            {/* S4AB: which background checks HR must upload in 4B. Compliance attaches no file. */}
            {!isClassificationOnly && (employeeType !== 'intern' ? (
              <div style={styles.fieldGroup} data-testid="s4a-required-checks">
                <h3 style={styles.groupTitle}>{s4t('section4ab.requiredTitle')}</h3>
                <p style={styles.hint}>{s4t('section4ab.requiredHint')}</p>
                <div style={{ opacity: s4.background_check_not_available ? 0.5 : 1, pointerEvents: s4.background_check_not_available ? 'none' : 'auto' }}>
                  <label style={{ ...styles.checkboxItem, marginTop: '12px' }}>
                    <input type="checkbox" checked={s4.global_check_required} data-testid="s4a-global-required"
                      onChange={e => setS4({ ...s4, global_check_required: e.target.checked })} />
                    {s4t('section4ab.globalRequired')}
                  </label>
                </div>
                {/* S4BGNOTAVAIL: unchanged alternative to the Global check, with its reason. */}
                <label style={{ ...styles.checkboxItem, marginTop: '12px' }}>
                  <input type="checkbox" checked={s4.background_check_not_available}
                    onChange={e => setS4({ ...s4, background_check_not_available: e.target.checked,
                      global_check_required: e.target.checked ? false : s4.global_check_required,
                      background_check_not_available_reason: e.target.checked ? s4.background_check_not_available_reason : '' })} />
                  No background check available
                </label>
                  {s4.background_check_not_available && (
                    <div style={{...styles.field, marginTop: '12px'}}>
                      <label style={styles.label} htmlFor={`${a11yId}-reason-required`}>Reason <span style={styles.required}>* required</span></label>
                      
                      <textarea id={`${a11yId}-reason-required`} style={{...styles.input, minHeight: '70px', resize: 'vertical'}}
                        value={s4.background_check_not_available_reason}
                        onChange={e => setS4({...s4, background_check_not_available_reason: e.target.value})}
                        placeholder="e.g. Employee joined before background checks were introduced; non-UK hire with no equivalent check available." />
                    </div>
                  )}
                <label style={{ ...styles.checkboxItem, marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e5e5e5' }}>
                  <input type="checkbox" checked={s4.local_check_required} data-testid="s4a-local-required"
                    onChange={e => setS4({ ...s4, local_check_required: e.target.checked })} />
                  {s4t('section4ab.localRequired')}
                </label>
              </div>
            ) : (
              <div style={styles.fieldGroup}>
                <div style={styles.internNote}>
                  Background check is not required for interns.
                </div>
              </div>
            ))}

            {/* Compliance Attestation - filled by the employee directly */}
            <div style={styles.fieldGroup}>
              <h3 style={styles.groupTitle}>Compliance Attestation</h3>
              <div style={styles.attestationPending}>
                <p style={styles.attestationPendingTitle}>Employee attestation form: coming soon</p>
                <p style={styles.attestationPendingText}>
                  The employee will complete this section directly on the platform.
                  The CoBOS compliance questions are being retrieved and will be added here before go-live.
                  Answers and submission date will be recorded automatically.
                </p>
              </div>
            </div>

            <div style={styles.btnRow}>
              {onBack && (
                <button
                  style={styles.backBtnRow}
                  onClick={onBack}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
                >
                  ← Back to dashboard
                </button>
              )}
              <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                {saving ? 'Saving...' : 'Save draft'}
              </button>
              <button style={styles.submitBtn} onClick={submit} disabled={loading || saving || !formData} data-testid="s4a-submit">
                {loading ? <><Spinner />Submitting...</> : (formData?.section4a_completed ? 'Update' : s4t('section4ab.submit4a'))}
              </button>
            </div>
            </>
            )}

            {/* ── SECTION 4B: BACKGROUND CHECK UPLOAD (HR) ── */}
            {s4Step === '4b' && !isClassificationOnly && (
              <div style={styles.fieldGroup} data-testid="s4b">
                <h3 style={styles.groupTitle}>{s4t('section4ab.title4b')}</h3>
                {(!formData?.section4b_state || formData.section4b_state === 'waiting') ? (
                  <p style={styles.hint} data-testid="s4b-waiting">{s4t('section4ab.waiting')}</p>
                ) : formData.section4b_state === 'not_required' ? (
                  <p style={styles.hint} data-testid="s4b-not-required">{s4t('section4ab.notRequired')}</p>
                ) : (
                  <>
                    {formData.section4b_state === 'complete' && (
                      <p style={{ ...styles.hint, color: '#2D4A3E' }} data-testid="s4b-complete">{s4t('section4ab.complete')}</p>
                    )}
                    {!canUpload4b && <p style={styles.hint} data-testid="s4b-hr-uploads">{s4t('section4ab.hrUploads')}</p>}
                    {(formData.section4b_required_checks || []).map(kind => {
                      const isGlobal = kind === 'Global';
                      const onFile = isGlobal ? formData.global_check_file_name : formData.local_check_file_name;
                      const chosen = isGlobal ? globalCheckFile : localCheckFile;
                      const inputId = `${a11yId}-s4b-${kind.toLowerCase()}`;
                      return (
                        <div key={kind} style={{ ...styles.field, marginTop: '14px' }} data-testid={`s4b-check-${kind.toLowerCase()}`}>
                          <label style={styles.label} htmlFor={inputId}>
                            {s4t(isGlobal ? 'section4ab.global' : 'section4ab.local')}
                          </label>
                          <p style={styles.fileName}>
                            {onFile ? s4t('section4ab.onFile', { name: onFile }) : s4t('section4ab.notUploaded')}
                          </p>
                          {canUpload4b && (
                            <>
                              <input id={inputId} type="file" accept=".pdf,.doc,.docx,.jpg,.png" style={styles.fileInput}
                                onChange={e => (isGlobal ? setGlobalCheckFile : setLocalCheckFile)(e.target.files[0] || null)} />
                              {chosen && <p style={styles.fileName}>Selected: {chosen.name}</p>}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
                <div style={styles.btnRow}>
                  {onBack && (
                    <button
                      style={styles.backBtnRow}
                      onClick={onBack}
                      onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                      onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
                    >
                      ← Back to dashboard
                    </button>
                  )}
                  {canUpload4b && ['pending', 'complete'].includes(formData?.section4b_state) && (
                    <button style={styles.submitBtn} onClick={uploadSection4b} disabled={loading || !formData} data-testid="s4b-upload">
                      {loading ? <><Spinner />{s4t('section4ab.uploading')}</> : s4t('section4ab.upload')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    maxWidth: '1100px', margin: '0 auto', padding: '24px',
    backgroundColor: '#f8f9fa'
  },
  backBtn: {
    background: 'none', border: '1px solid rgba(26,39,68,0.25)', color: NAVY_FORM,
    fontSize: '14px', cursor: 'pointer', padding: '9px 18px',
    borderRadius: '6px', marginBottom: '24px', fontWeight: '600'
  },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  subtitle: { fontSize: '16px', color: '#666', margin: '4px 0 0 0' },
  progress: {
    display: 'flex', gap: '24px', marginBottom: '32px',
    backgroundColor: '#fff', padding: '20px', borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flexWrap: 'wrap'
  },
  progressItem: { display: 'flex', alignItems: 'center', gap: '10px' },
  progressDot: {
    width: '32px', height: '32px', borderRadius: '50%', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '600', flexShrink: 0
  },
  progressLabel: { fontSize: '13px', fontWeight: '500', margin: '0 0 2px 0' },
  progressOwner: { fontSize: '11px', color: '#aaa', margin: '0' },
  // S4SPLIT: the two-step sub-indicator inside Section 4 (Classification -> Background Check).
  s4Steps: {
    display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap',
    margin: '4px 0 20px 0', paddingBottom: '16px', borderBottom: '1px solid #eee',
  },
  s4StepItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  s4StepDot: {
    width: '26px', height: '26px', borderRadius: '50%', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '600', flexShrink: 0,
  },
  s4StepLabel: { fontSize: '13px', fontWeight: '500' },
  form: {
    backgroundColor: '#fff', borderRadius: '8px',
    padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  formHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '24px'
  },
  formTitle: { fontSize: '18px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  demoBtn: {
    backgroundColor: '#f0f4ff', color: NAVY_FORM,
    border: '1px solid #c7d4f5', borderRadius: '6px',
    padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '500'
  },
  fieldGroup: {
    marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #eee'
  },
  groupTitle: {
    fontSize: '14px', fontWeight: '600', color: '#666',
    textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 16px 0'
  },
  subBlockLabel: {
    fontSize: '9px', fontWeight: '700', color: NAVY_SHELL,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    opacity: 0.5, margin: '0 0 14px 0'
  },
  collapsibleHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', userSelect: 'none',
  },
  chevron: {
    fontSize: '10px', color: NAVY_SHELL, opacity: 0.5,
    transition: 'transform 0.3s ease', display: 'inline-block',
  },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '16px' },
  label: {
    display: 'block', fontSize: '13px', fontWeight: '500',
    color: '#333', marginBottom: '6px'
  },
  hint: { fontSize: '12px', color: '#888', margin: '8px 0 0 0' },
  groupSubtitle: {
    fontSize: '13px', fontWeight: '600', color: '#444',
    margin: '0 0 10px 0'
  },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none'
  },
  textarea: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
    outline: 'none', resize: 'vertical'
  },
  checkboxGroup: { display: 'flex', flexDirection: 'column', gap: '12px' },
  radioGroup: { display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' },
  checkboxItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '14px', cursor: 'pointer', color: '#333'
  },
  btnRow: { display: 'flex', gap: '12px', marginTop: '8px' },
  // FORMBTNSHARE: the action-row trio now spreads the ONE shared definition from theme.js so
  // Back/Save draft/Submit are byte-identical here and across SponsorForm + LeaverForm. backBtnRow
  // is the row-context Back (page-header backBtn above keeps its own header styling).
  backBtnRow: { ...FORM_BACK_BTN },
  saveBtn: { ...FORM_SAVE_BTN },
  submitBtn: { ...FORM_SUBMIT_BTN },
  error: {
    backgroundColor: '#fde8e8', border: '1px solid #e74c3c',
    borderRadius: '6px', padding: '12px 16px', fontSize: '14px',
    color: '#c0392b', marginBottom: '16px'
  },
  successMsg: {
    backgroundColor: '#e8f8f0', border: '1px solid #27ae60',
    borderRadius: '6px', padding: '12px 16px', fontSize: '14px',
    color: '#1a7a45', marginBottom: '16px'
  },
  accessDenied: {
    backgroundColor: '#fff', borderRadius: '8px', padding: '40px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center'
  },
  accessDeniedTitle: {
    fontSize: '18px', fontWeight: '600', color: '#e74c3c', margin: '0 0 8px 0'
  },
  accessDeniedMsg: { fontSize: '14px', color: '#666', margin: '0' },
  fileInput: {
    display: 'block', width: '100%', padding: '8px 0',
    fontSize: '14px', color: '#333', cursor: 'pointer'
  },
  fileName: { fontSize: '12px', color: '#27ae60', margin: '6px 0 0 0', fontWeight: '500' },
  required: { color: '#e74c3c', fontSize: '12px', fontWeight: '400', marginLeft: '4px' },
  internNote: {
    backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5',
    borderRadius: '6px', padding: '12px 16px',
    fontSize: '13px', color: '#555', fontStyle: 'italic'
  },
  attestationPending: {
    backgroundColor: '#fffbf0', border: '1px solid #f39c12',
    borderRadius: '8px', padding: '16px 20px'
  },
  attestationPendingTitle: {
    fontSize: '14px', fontWeight: '600', color: '#856404', margin: '0 0 6px 0'
  },
  attestationPendingText: {
    fontSize: '13px', color: '#666', margin: '0', lineHeight: '1.6'
  },
  submittedBadge: {
    backgroundColor: '#edfaf1', color: '#27ae60', border: '1px solid #27ae60',
    borderRadius: '20px', padding: '4px 14px', fontSize: '13px', fontWeight: '600'
  },
};

export default NewStarterForm;
